import React, { useEffect, useState } from "react";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import {
  BlockDes,
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
} from "../../../components/Component";
import {
  Card,
  Col,
  Row,
  Table,
  Tag,
  Statistic,
  Empty,
  Spin,
  Switch,
  Carousel,
  Progress,
  Drawer,
} from "antd";
import ReactECharts from "echarts-for-react";
import {
  LoadingOutlined,
  BarChartOutlined,
  TableOutlined,
} from "@ant-design/icons";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router";
import { updateMenuData } from "../../../store/slices/misc/menuData";
import TopMenu from "../../../route/topMenu";
import { dashboardPMSApis } from "../../../api/DashboardPMS";

const ConsolidatedDashboard = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  // Navigate to one of the per-project pages. projectId is preserved in both
  // the URL param and route state so child pages (which may read either) work.
  const goToProjectPage = (
    record: any,
    page: "dashboard" | "budget-analysis" | "inventory-analysis"
  ) => {
    const projectId = record?.projectId ?? record?.id;
    if (!projectId) return;
    navigate(`/pms/project/${page}/${projectId}`, {
      state: {
        ...(location.state || {}),
        id: projectId,
        projectId,
        projectName: record?.projectName ?? record?.project,
        siteId: record?.siteId,
        orgId: record?.orgId,
        fromDashboard: true,
      },
    });
  };
  const [loading, setLoading] = useState(false);
  const [projectMgmtViewMode, setProjectMgmtViewMode] = useState<
    "graph" | "table"
  >("graph");
  const [procurementViewMode, setProcurementViewMode] = useState<
    "graph" | "table"
  >("graph");
  const [workOrderViewMode, setWorkOrderViewMode] = useState<"graph" | "table">(
    "graph"
  );
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedProject, setSelectedProject] = useState<any>(null);
  const [inventoryDrawerVisible, setInventoryDrawerVisible] = useState(false);
  const [inventoryDrawerData, setInventoryDrawerData] = useState<any>(null);
  const [inventoryDrawerType, setInventoryDrawerType] = useState<string>("");
  const [budgetDrawerVisible, setBudgetDrawerVisible] = useState(false);
  const [selectedBudgetProject, setSelectedBudgetProject] = useState<any>(null);
  const [varianceDrawerVisible, setVarianceDrawerVisible] = useState(false);
  const [selectedVarianceProject, setSelectedVarianceProject] =
    useState<any>(null);
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);
  const [procurementDrawerVisible, setProcurementDrawerVisible] =
    useState(false);
  const [selectedProcurementProject, setSelectedProcurementProject] =
    useState<any>(null);
  const [raBillDrawerVisible, setRaBillDrawerVisible] = useState(false);
  const [selectedRaBillProject, setSelectedRaBillProject] = useState<any>(null);
  const [poDrawerVisible, setPoDrawerVisible] = useState(false);
  const [selectedPoProject, setSelectedPoProject] = useState<any>(null);
  const [invoicePayablesDrawerVisible, setInvoicePayablesDrawerVisible] =
    useState(false);
  const [selectedInvoicePayablesProject, setSelectedInvoicePayablesProject] =
    useState<any>(null);
  const [raBillPayablesDrawerVisible, setRaBillPayablesDrawerVisible] =
    useState(false);
  const [selectedRaBillPayablesProject, setSelectedRaBillPayablesProject] =
    useState<any>(null);

  useEffect(() => {
    dispatch(
      updateMenuData(
        TopMenu?.activeProjects?.map((item: any) => {
          return {
            ...item,
            linkState: location.state,
          };
        })
      )
    );
  }, [location]);


  // Route-state passthrough — used ONLY by drill-down endpoints that recognize
  // those filters. Main landing reports must NOT use this.
  const linkStateParams = (): Record<string, string> => {
    const linkState: any = location.state || {};
    const params: Record<string, string> = {};
    if (linkState.siteIds) params.siteIds = String(linkState.siteIds);
    else if (linkState.siteId) params.siteIds = String(linkState.siteId);
    if (linkState.projectIds) params.projectIds = String(linkState.projectIds);
    else if (linkState.projectId)
      params.projectIds = String(linkState.projectId);
    else if (linkState.id) params.projectIds = String(linkState.id);
    return params;
  };

  const extractDashboardRows = (apiResponse: any): any[] => {
    if (!apiResponse) return [];
    if (apiResponse.status === false) return [];
    const inner = apiResponse.data;
    if (!inner) return [];
    if (Array.isArray(inner)) return inner;
    if (Array.isArray(inner.data)) return inner.data;
    if (Array.isArray(inner.rows)) return inner.rows;
    if (Array.isArray(inner.list)) return inner.list;
    return [];
  };

  const extractDashboardTotal = (apiResponse: any): number => {
    if (!apiResponse) return 0;
    const inner = apiResponse.data;
    if (!inner) return 0;
    const t = inner.totalCount ?? inner.total ?? inner.recordCount ?? 0;
    return Number(t) || 0;
  };

  // Pagination defaults — two flavors:
  //  • Section tables (main dashboard) always show the paginator, even with
  //    one page, so users can change pageSize.
  //  • Drawer / sub-report tables hide the paginator when the response fits
  //    in one page (hideOnSinglePage: true) to keep small lists clean.
  // Both are factory functions so each useState gets its own mutable object.
  const buildSectionPagination = () => ({
    current: 1 as number,
    pageSize: 10 as number,
    showSizeChanger: true,
    hideOnSinglePage: false,
    pageSizeOptions: ["10", "20", "30", "50", "100"] as string[],
    total: 0 as number,
  });

  const buildDefaultPagination = () => ({
    current: 1 as number,
    pageSize: 10 as number,
    showSizeChanger: true,
    hideOnSinglePage: true,
    pageSizeOptions: ["10", "20", "30", "50", "100"] as string[],
    total: 0 as number,
  });

  // Coerce a currency cell to a number whether it's a raw number from the API
  // or a "₹1,20,000"-style formatted string from the legacy mock data.
  const toNumber = (value: any): number => {
    if (value === null || value === undefined || value === "") return 0;
    if (typeof value === "number") return Number.isNaN(value) ? 0 : value;
    const parsed = parseFloat(String(value).replace(/[₹,\s]/g, ""));
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const formatExecCurrency = (value: any): string => {
    if (value === null || value === undefined || value === "") return "-";
    const num = typeof value === "number" ? value : Number(value);
    if (Number.isNaN(num)) return String(value);
    return `₹${num.toLocaleString("en-IN")}`;
  };

  const formatExecPercent = (value: any): string => {
    if (value === null || value === undefined || value === "") return "0%";
    const num = typeof value === "number" ? value : Number(value);
    if (Number.isNaN(num)) return String(value);
    return `${Math.round(num)}%`;
  };

  // ----------------------------- Executive Summary -----------------------------
  const [executiveSummaryData, setExecutiveSummaryData] = useState<any[]>([]);
  const [executiveSummaryLoading, setExecutiveSummaryLoading] = useState(false);
  const [executiveSummaryPagination, setExecutiveSummaryPagination] = useState(buildSectionPagination());

  const fetchExecutiveSummary = async (
    page = executiveSummaryPagination.current,
    pageSize = executiveSummaryPagination.pageSize
  ) => {
    setExecutiveSummaryLoading(true);
    try {
      // executive-summary is org-scoped — no filters per doc §1.
      const apiResponse: any = await dashboardPMSApis.fetchExecutiveSummary({
        page,
        pageSize,
      });
      const rows = extractDashboardRows(apiResponse);
      const total = extractDashboardTotal(apiResponse);
      setExecutiveSummaryPagination((prev) => ({
        ...prev,
        current: page,
        pageSize,
        total,
      }));
      const mapped = rows.map((row: any, idx: number) => ({
        key: String(row.projectId ?? row.id ?? idx),
        projectName: row.projectName ?? row.project_name ?? row.name ?? "-",
        currentProgress: formatExecPercent(
          row.currentProgress ?? row.progress ?? row.progressPercent ?? 0
        ),
        budgetValue: formatExecCurrency(
          row.budgetValue ?? row.budget ?? row.totalBudget
        ),
        budgetUtilization: formatExecCurrency(
          row.budgetUtilization ?? row.utilizedBudget ?? row.budgetUtilized
        ),
        poValue: formatExecCurrency(row.poValue ?? row.totalPoValue ?? row.po),
        invoicePayables: formatExecCurrency(
          row.invoicePayables ?? row.invoicePayable ?? row.payables
        ),
        woValue: formatExecCurrency(
          row.woValue ?? row.workOrderValue ?? row.wo
        ),
        raBillsPayable: formatExecCurrency(
          row.raBillsPayable ?? row.raBillPayable ?? row.raBills
        ),
        orgId: row.orgId,
        siteId: row.siteId,
        projectId: row.projectId,
      }));
      setExecutiveSummaryData(mapped);
    } catch (error) {
      console.log("fetchExecutiveSummary error:", error);
      setExecutiveSummaryData([]);
    } finally {
      setExecutiveSummaryLoading(false);
    }
  };

  // Executive Summary pagination change handler
  const handleExecutiveSummaryTableChange = (newPagination: any) => {
    fetchExecutiveSummary(newPagination.current, newPagination.pageSize);
  };

  // ----------------------------- Project Management Analytics -----------------------------
  const [projectAnalyticsGraphData, setProjectAnalyticsGraphData] = useState<
    any[]
  >([]);
  const [projectAnalyticsTabularData, setProjectAnalyticsTabularData] =
    useState<any[]>([]);
  const [projectAnalyticsLoading, setProjectAnalyticsLoading] = useState(false);
  const [projectAnalyticsPagination, setProjectAnalyticsPagination] = useState(buildSectionPagination());

  // Graph fetch — once, unpaginated (per doc).
  const fetchProjectAnalyticsGraph = async () => {
    try {
      const graphRes: any =
        await dashboardPMSApis.fetchProjectAnalyticsGraph();
      setProjectAnalyticsGraphData(extractDashboardRows(graphRes));
    } catch (error) {
      console.log("fetchProjectAnalyticsGraph error:", error);
      setProjectAnalyticsGraphData([]);
    }
  };

  // Tabular fetch — paginated, refetched on page/pageSize change.
  const fetchProjectAnalyticsTabular = async (
    page = projectAnalyticsPagination.current,
    pageSize = projectAnalyticsPagination.pageSize
  ) => {
    setProjectAnalyticsLoading(true);
    try {
      const tabularRes: any =
        await dashboardPMSApis.fetchProjectAnalyticsTabular({ page, pageSize });
      const rows = extractDashboardRows(tabularRes);
      const total = extractDashboardTotal(tabularRes);
      setProjectAnalyticsPagination((prev) => ({
        ...prev,
        current: page,
        pageSize,
        total,
      }));
      const tabRows = rows.map((row: any, idx: number) => ({
        key: String(row.projectId ?? row.id ?? idx),
        projectName: row.projectName ?? row.project ?? row.name ?? "-",
        actualProgress: formatExecPercent(row.actualProgress),
        avgDelayDays: Number(row.avgDelayDays ?? 0),
        overallStatus: row.overallStatus ?? row.status ?? "-",
        orgId: row.orgId,
        projectId: row.projectId,
      }));
      setProjectAnalyticsTabularData(tabRows);
    } catch (error) {
      console.log("fetchProjectAnalyticsTabular error:", error);
      setProjectAnalyticsTabularData([]);
    } finally {
      setProjectAnalyticsLoading(false);
    }
  };

  const handleProjectAnalyticsTableChange = (newPagination: any) => {
    fetchProjectAnalyticsTabular(newPagination.current, newPagination.pageSize);
  };

  // Project drill-down state
  const [selectedProjectKpi, setSelectedProjectKpi] = useState<any>(null);
  const [selectedProjectPhases, setSelectedProjectPhases] = useState<any[]>([]);
  const [projectDrillLoading, setProjectDrillLoading] = useState(false);

  const fetchProjectDrillDown = async (projectId: any) => {
    if (!projectId) return;
    setProjectDrillLoading(true);
    try {
      const [kpiRes, phaseRes]: any[] = await Promise.all([
        dashboardPMSApis.fetchProjectKpiSummary({
          projectIds: String(projectId),
        }),
        dashboardPMSApis.fetchProjectPhaseDetails({
          projectIds: String(projectId),
        }),
      ]);
      const kpiRows = extractDashboardRows(kpiRes);
      setSelectedProjectKpi(kpiRows[0] || null);
      const phaseRows = extractDashboardRows(phaseRes).map(
        (row: any, idx: number) => ({
          key: `phase-${row.phaseId ?? idx}`,
          phaseId: row.phaseId,
          projectId: row.projectId,
          projectName: row.name ?? "-",
          actualProgress: formatExecPercent(row.actualProgress),
          avgDelayDays: Number(row.avgDelayDays ?? 0),
          overallStatus: row.status ?? "-",
          type: "phase",
          // Placeholder so antd renders an expand caret; replaced lazily.
          children: [
            {
              key: `phase-${row.phaseId ?? idx}-loading`,
              projectName: "Loading…",
              type: "activity",
              __placeholder: true,
            },
          ],
        })
      );
      setSelectedProjectPhases(phaseRows);
    } catch (error) {
      console.log("fetchProjectDrillDown error:", error);
      setSelectedProjectKpi(null);
      setSelectedProjectPhases([]);
    } finally {
      setProjectDrillLoading(false);
    }
  };

  const fetchPhaseActivities = async (projectId: any, phaseId: any) => {
    if (!projectId || !phaseId) return [];
    try {
      const res: any = await dashboardPMSApis.fetchProjectActivityDetails({
        projectIds: String(projectId),
        phaseIds: String(phaseId),
      });
      return extractDashboardRows(res).map((row: any, idx: number) => ({
        key: `activity-${row.activityId ?? idx}`,
        activityId: row.activityId,
        phaseId: row.phaseId,
        projectId: row.projectId,
        projectName: row.name ?? "-",
        actualProgress: formatExecPercent(row.actualProgress),
        avgDelayDays: Number(row.delayedDays ?? row.avgDelayDays ?? 0),
        overallStatus: row.status ?? "-",
        type: "activity",
        children: [
          {
            key: `activity-${row.activityId ?? idx}-loading`,
            projectName: "Loading…",
            type: "task",
            __placeholder: true,
          },
        ],
      }));
    } catch (error) {
      console.log("fetchPhaseActivities error:", error);
      return [];
    }
  };

  const fetchActivityTasks = async (
    projectId: any,
    phaseId: any,
    activityId: any
  ) => {
    if (!projectId || !phaseId || !activityId) return [];
    try {
      const res: any = await dashboardPMSApis.fetchProjectTaskDetails({
        projectIds: String(projectId),
        phaseIds: String(phaseId),
        activityIds: String(activityId),
      });
      return extractDashboardRows(res).map((row: any, idx: number) => ({
        key: `task-${row.taskId ?? idx}`,
        taskId: row.taskId,
        projectName: row.name ?? "-",
        actualProgress: formatExecPercent(row.actualProgress),
        avgDelayDays: Number(row.delayDays ?? 0),
        overallStatus: row.status ?? "-",
        type: "task",
      }));
    } catch (error) {
      console.log("fetchActivityTasks error:", error);
      return [];
    }
  };

  // ----------------------------- Procurement & Payables -----------------------------
  const [procurementGraphData, setProcurementGraphData] = useState<any[]>([]);
  const [procurementTabularData, setProcurementTabularData] = useState<any[]>(
    []
  );
  const [procurementLoading, setProcurementLoading] = useState(false);

  const [procurementPagination, setProcurementPagination] = useState(buildSectionPagination());

  const fetchProcurementPayablesGraph = async () => {
    try {
      const graphRes: any =
        await dashboardPMSApis.fetchProcurementPayablesGraph();
      setProcurementGraphData(extractDashboardRows(graphRes));
    } catch (error) {
      console.log("fetchProcurementPayablesGraph error:", error);
      setProcurementGraphData([]);
    }
  };

  const fetchProcurementPayablesTabular = async (
    page = procurementPagination.current,
    pageSize = procurementPagination.pageSize
  ) => {
    setProcurementLoading(true);
    try {
      const tabularRes: any =
        await dashboardPMSApis.fetchProcurementPayablesTabular({
          page,
          pageSize,
        });
      const rows = extractDashboardRows(tabularRes);
      const total = extractDashboardTotal(tabularRes);
      setProcurementPagination((prev) => ({
        ...prev,
        current: page,
        pageSize,
        total,
      }));
      const tabRows = rows.map((row: any, idx: number) => ({
        key: String(row.projectId ?? row.siteId ?? idx),
        projectName: row.projectName ?? row.project ?? "-",
        poCount: Number(row.poCount ?? 0),
        poValue: formatExecCurrency(row.poValue),
        invoicePayablesCount: Number(row.invoiceCount ?? 0),
        invoicePayablesValue: formatExecCurrency(row.invoiceValue),
        onTimeValue: formatExecCurrency(row.onTimeValue),
        onTimePercent: formatExecPercent(row.onTimePct),
        outstandingValue: formatExecCurrency(row.outstandingValue),
        outstandingPercent: formatExecPercent(row.outstandingPct),
        overdueValue: formatExecCurrency(row.overdueValue),
        overduePercent: formatExecPercent(row.overduePct),
        avgAgingDays: Number(row.avgAgingDays ?? 0),
        orgId: row.orgId,
        siteId: row.siteId,
        projectId: row.projectId,
      }));
      setProcurementTabularData(tabRows);
    } catch (error) {
      console.log("fetchProcurementPayablesTabular error:", error);
      setProcurementTabularData([]);
    } finally {
      setProcurementLoading(false);
    }
  };

  const handleProcurementTableChange = (newPagination: any) => {
    fetchProcurementPayablesTabular(
      newPagination.current,
      newPagination.pageSize
    );
  };

  // PO drill-down
  const [poSummary, setPoSummary] = useState<any>(null);
  const [poDetails, setPoDetails] = useState<any[]>([]);
  const [poDrillLoading, setPoDrillLoading] = useState(false);

  const [poDrillSiteId, setPoDrillSiteId] = useState<any>(null);
  const [poPagination, setPoPagination] = useState(buildDefaultPagination());

  const fetchPoDrillDown = async (
    siteId: any,
    page = 1,
    pageSize = poPagination.pageSize
  ) => {
    setPoDrillLoading(true);
    setPoDrillSiteId(siteId);
    try {
      const params = { siteIds: siteId ? String(siteId) : undefined };
      const [sumRes, detRes]: any[] = await Promise.all([
        dashboardPMSApis.fetchPoSummary(params),
        dashboardPMSApis.fetchPoDetails({ ...params, page, pageSize }),
      ]);
      setPoSummary(extractDashboardRows(sumRes)[0] || null);
      setPoPagination((prev) => ({
        ...prev,
        current: page,
        pageSize,
        total: extractDashboardTotal(detRes),
      }));
      setPoDetails(
        extractDashboardRows(detRes).map((row: any, idx: number) => ({
          key: `po-${row.poNumber ?? idx}`,
          ...row,
        }))
      );
    } catch (error) {
      console.log("fetchPoDrillDown error:", error);
      setPoSummary(null);
      setPoDetails([]);
    } finally {
      setPoDrillLoading(false);
    }
  };

  const handlePoTableChange = (newPagination: any) =>
    fetchPoDrillDown(
      poDrillSiteId,
      newPagination.current,
      newPagination.pageSize
    );

  // Invoice drill-down
  const [invoiceSummary, setInvoiceSummary] = useState<any>(null);
  const [invoiceDetailsList, setInvoiceDetailsList] = useState<any[]>([]);
  const [invoiceDrillLoading, setInvoiceDrillLoading] = useState(false);
  const [invoiceDrillSiteId, setInvoiceDrillSiteId] = useState<any>(null);
  const [invoicePagination, setInvoicePagination] = useState(
    buildDefaultPagination()
  );

  const fetchInvoiceDrillDown = async (
    siteId: any,
    page = 1,
    pageSize = invoicePagination.pageSize
  ) => {
    setInvoiceDrillLoading(true);
    setInvoiceDrillSiteId(siteId);
    try {
      const params = { siteIds: siteId ? String(siteId) : undefined };
      const [sumRes, detRes]: any[] = await Promise.all([
        dashboardPMSApis.fetchInvoiceSummary(params),
        dashboardPMSApis.fetchInvoiceDetails({ ...params, page, pageSize }),
      ]);
      setInvoiceSummary(extractDashboardRows(sumRes)[0] || null);
      setInvoicePagination((prev) => ({
        ...prev,
        current: page,
        pageSize,
        total: extractDashboardTotal(detRes),
      }));
      setInvoiceDetailsList(
        extractDashboardRows(detRes).map((row: any, idx: number) => ({
          key: `inv-${row.invoiceNumber ?? idx}`,
          ...row,
        }))
      );
    } catch (error) {
      console.log("fetchInvoiceDrillDown error:", error);
      setInvoiceSummary(null);
      setInvoiceDetailsList([]);
    } finally {
      setInvoiceDrillLoading(false);
    }
  };

  const handleInvoiceTableChange = (newPagination: any) =>
    fetchInvoiceDrillDown(
      invoiceDrillSiteId,
      newPagination.current,
      newPagination.pageSize
    );

  // Invoice aging drill-down
  const [invoiceAgingSummary, setInvoiceAgingSummary] = useState<any>(null);
  const [invoiceAgingDetailsList, setInvoiceAgingDetailsList] = useState<any[]>(
    []
  );
  const [invoiceAgingLoading, setInvoiceAgingLoading] = useState(false);
  const [invoiceAgingSiteId, setInvoiceAgingSiteId] = useState<any>(null);
  const [invoiceAgingPagination, setInvoiceAgingPagination] = useState(
    buildDefaultPagination()
  );

  const fetchInvoiceAgingDrillDown = async (
    siteId: any,
    page = 1,
    pageSize = invoiceAgingPagination.pageSize
  ) => {
    setInvoiceAgingLoading(true);
    setInvoiceAgingSiteId(siteId);
    try {
      const params = { siteIds: siteId ? String(siteId) : undefined };
      const [sumRes, detRes]: any[] = await Promise.all([
        dashboardPMSApis.fetchInvoiceAgingSummary(params),
        dashboardPMSApis.fetchInvoiceAgingDetails({
          ...params,
          page,
          pageSize,
        }),
      ]);
      setInvoiceAgingSummary(extractDashboardRows(sumRes)[0] || null);
      setInvoiceAgingPagination((prev) => ({
        ...prev,
        current: page,
        pageSize,
        total: extractDashboardTotal(detRes),
      }));
      setInvoiceAgingDetailsList(
        extractDashboardRows(detRes).map((row: any, idx: number) => ({
          key: `inva-${row.invoiceNumber ?? idx}`,
          ...row,
        }))
      );
    } catch (error) {
      console.log("fetchInvoiceAgingDrillDown error:", error);
      setInvoiceAgingSummary(null);
      setInvoiceAgingDetailsList([]);
    } finally {
      setInvoiceAgingLoading(false);
    }
  };

  const handleInvoiceAgingTableChange = (newPagination: any) =>
    fetchInvoiceAgingDrillDown(
      invoiceAgingSiteId,
      newPagination.current,
      newPagination.pageSize
    );

  // ----------------------------- Inventory Analytics -----------------------------
  const [inventorySummaryData, setInventorySummaryData] = useState<any[]>([]);
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const [inventoryDrillRows, setInventoryDrillRows] = useState<any[]>([]);
  const [inventoryDrillLoading, setInventoryDrillLoading] = useState(false);

  const [inventoryPagination, setInventoryPagination] = useState(buildSectionPagination());

  const fetchInventorySummary = async (
    page = inventoryPagination.current,
    pageSize = inventoryPagination.pageSize
  ) => {
    setInventoryLoading(true);
    try {
      // inventory-summary is org-scoped — no filters per doc §4.1.
      const res: any = await dashboardPMSApis.fetchInventorySummary({
        page,
        pageSize,
      });
      const total = extractDashboardTotal(res);
      setInventoryPagination((prev) => ({
        ...prev,
        current: page,
        pageSize,
        total,
      }));
      const rows = extractDashboardRows(res).map((row: any, idx: number) => ({
        key: String(row.projectId ?? idx),
        projectName: row.projectName ?? "-",
        shortage: `${row.shortagePct ?? 0}%`,
        overstock: `${row.overstockPct ?? 0}%`,
        avgStockAge: `${row.avgStockAge ?? 0} days`,
        turnoverRate: `${row.turnoverRate ?? 0}x`,
        orgId: row.orgId,
        siteId: row.siteId,
        projectId: row.projectId,
      }));
      setInventorySummaryData(rows);
    } catch (error) {
      console.log("fetchInventorySummary error:", error);
      setInventorySummaryData([]);
    } finally {
      setInventoryLoading(false);
    }
  };

  const handleInventoryTableChange = (newPagination: any) => {
    fetchInventorySummary(newPagination.current, newPagination.pageSize);
  };

  // Inventory drill-down — remember type + siteId so pagination changes
  // can re-fetch with the right endpoint/scope.
  const [inventoryDrillType, setInventoryDrillType] = useState<
    "shortage" | "overstock" | "stockAge" | "turnover" | null
  >(null);
  const [inventoryDrillSiteId, setInventoryDrillSiteId] = useState<any>(null);
  const [inventoryDrillPagination, setInventoryDrillPagination] = useState(
    buildDefaultPagination()
  );

  const fetchInventoryDrillDown = async (
    type: "shortage" | "overstock" | "stockAge" | "turnover",
    siteId: any,
    page = 1,
    pageSize = inventoryDrillPagination.pageSize
  ) => {
    setInventoryDrillLoading(true);
    setInventoryDrillType(type);
    setInventoryDrillSiteId(siteId);
    try {
      const params = {
        siteIds: siteId ? String(siteId) : undefined,
        page,
        pageSize,
      };
      let res: any = null;
      if (type === "shortage")
        res = await dashboardPMSApis.fetchInventoryShortageDetails(params);
      else if (type === "overstock")
        res = await dashboardPMSApis.fetchInventoryOverstockDetails(params);
      else if (type === "stockAge")
        res = await dashboardPMSApis.fetchInventoryStockAgeDetails(params);
      else if (type === "turnover")
        res = await dashboardPMSApis.fetchInventoryTurnoverDetails(params);
      setInventoryDrillPagination((prev) => ({
        ...prev,
        current: page,
        pageSize,
        total: extractDashboardTotal(res),
      }));
      const rows = extractDashboardRows(res).map((row: any, idx: number) => ({
        key: `inv-${type}-${idx}`,
        ...row,
        shortagePercent:
          row.shortagePct !== undefined ? `${row.shortagePct}%` : undefined,
        overstockPercent:
          row.overstockPct !== undefined ? `${row.overstockPct}%` : undefined,
        daysInInventory: toNumber(row.daysInInventory ?? row.agingDays),
        agingDays: toNumber(row.agingDays),
      }));
      setInventoryDrillRows(rows);
    } catch (error) {
      console.log("fetchInventoryDrillDown error:", error);
      setInventoryDrillRows([]);
    } finally {
      setInventoryDrillLoading(false);
    }
  };

  const handleInventoryDrillTableChange = (newPagination: any) => {
    if (!inventoryDrillType) return;
    fetchInventoryDrillDown(
      inventoryDrillType,
      inventoryDrillSiteId,
      newPagination.current,
      newPagination.pageSize
    );
  };

  // ----------------------------- WO & RA Bills -----------------------------
  const [woRaGraphData, setWoRaGraphData] = useState<any[]>([]);
  const [woRaTabularData, setWoRaTabularData] = useState<any[]>([]);
  const [woRaLoading, setWoRaLoading] = useState(false);

  const [woRaPagination, setWoRaPagination] = useState(buildSectionPagination());

  const fetchWoRaBillsGraph = async () => {
    try {
      const graphRes: any = await dashboardPMSApis.fetchWoRaBillsGraph();
      setWoRaGraphData(extractDashboardRows(graphRes));
    } catch (error) {
      console.log("fetchWoRaBillsGraph error:", error);
      setWoRaGraphData([]);
    }
  };

  const fetchWoRaBillsTabular = async (
    page = woRaPagination.current,
    pageSize = woRaPagination.pageSize
  ) => {
    setWoRaLoading(true);
    try {
      const tabularRes: any = await dashboardPMSApis.fetchWoRaBillsTabular({
        page,
        pageSize,
      });
      const rows = extractDashboardRows(tabularRes);
      const total = extractDashboardTotal(tabularRes);
      setWoRaPagination((prev) => ({
        ...prev,
        current: page,
        pageSize,
        total,
      }));
      const tabRows = rows.map((row: any, idx: number) => ({
        key: String(row.projectId ?? idx),
        projectName: row.project ?? row.projectName ?? "-",
        woCount: Number(row.woCount ?? 0),
        woValue: formatExecCurrency(row.woValue),
        executionValue: formatExecCurrency(row.executionValue),
        executionPercent: formatExecPercent(row.executionPct),
        raBillsRaised: formatExecCurrency(row.raBillsRaised),
        raBillsApproved: formatExecCurrency(row.raBillsApproved),
        raBillsPayable: formatExecCurrency(row.raBillsPayable),
        raBillsOutstanding: formatExecCurrency(row.outstanding),
        raBillsOverdue: formatExecCurrency(row.overdue),
        avgAgingDays: Number(row.avgAgingDays ?? 0),
        orgId: row.orgId,
        siteId: row.siteId,
        projectId: row.projectId,
        contractors: [],
      }));
      setWoRaTabularData(tabRows);
    } catch (error) {
      console.log("fetchWoRaBillsTabular error:", error);
      setWoRaTabularData([]);
    } finally {
      setWoRaLoading(false);
    }
  };

  const handleWoRaTableChange = (newPagination: any) => {
    fetchWoRaBillsTabular(newPagination.current, newPagination.pageSize);
  };

  // WO drill-down (contractors per row)
  const fetchWoContractorsForRow = async (recordKey: string, siteId: any) => {
    try {
      const res: any = await dashboardPMSApis.fetchWoDetails({
        siteIds: siteId ? String(siteId) : undefined,
        page: 1,
        pageSize: 50,
      });
      const contractors = extractDashboardRows(res).map(
        (row: any, idx: number) => ({
          key: `wo-${row.woId ?? idx}`,
          contractorName: row.contractorName ?? "-",
          woNumber: row.workOrderNumber ?? "-",
          woValue: formatExecCurrency(row.woValue),
          executionPercent: formatExecPercent(row.executionPct),
          raBillsRaised: formatExecCurrency(row.raBillsRaised),
          raBillsApproved: formatExecCurrency(row.raBillsApproved),
          raBillsPayable: formatExecCurrency(row.raBillsPayable),
          outstanding: formatExecCurrency(row.outstanding),
          overdue: formatExecCurrency(row.overdue),
          agingDays: Number(row.avgAgingDays ?? 0),
        })
      );
      setWoRaTabularData((prev) =>
        prev.map((r) =>
          r.key === recordKey ? { ...r, contractors } : r
        )
      );
    } catch (error) {
      console.log("fetchWoContractorsForRow error:", error);
    }
  };

  // RA Bills Payable drill-down
  const [raBillsPayableSummary, setRaBillsPayableSummary] = useState<any>(null);
  const [raBillsPayableList, setRaBillsPayableList] = useState<any[]>([]);
  const [raBillsPayableLoading, setRaBillsPayableLoading] = useState(false);
  const [raBillsPayableSiteId, setRaBillsPayableSiteId] = useState<any>(null);
  const [raBillsPayablePagination, setRaBillsPayablePagination] = useState(
    buildDefaultPagination()
  );

  const fetchRaBillsPayableDrillDown = async (
    siteId: any,
    page = 1,
    pageSize = raBillsPayablePagination.pageSize
  ) => {
    setRaBillsPayableLoading(true);
    setRaBillsPayableSiteId(siteId);
    try {
      const params = { siteIds: siteId ? String(siteId) : undefined };
      const [sumRes, detRes]: any[] = await Promise.all([
        dashboardPMSApis.fetchRaBillsPayableSummary(params),
        dashboardPMSApis.fetchRaBillsPayableDetails({
          ...params,
          page,
          pageSize,
        }),
      ]);
      setRaBillsPayableSummary(extractDashboardRows(sumRes)[0] || null);
      setRaBillsPayablePagination((prev) => ({
        ...prev,
        current: page,
        pageSize,
        total: extractDashboardTotal(detRes),
      }));
      setRaBillsPayableList(
        extractDashboardRows(detRes).map((row: any, idx: number) => ({
          key: `rabp-${row.raBillId ?? idx}`,
          ...row,
        }))
      );
    } catch (error) {
      console.log("fetchRaBillsPayableDrillDown error:", error);
      setRaBillsPayableSummary(null);
      setRaBillsPayableList([]);
    } finally {
      setRaBillsPayableLoading(false);
    }
  };

  const handleRaBillsPayableTableChange = (newPagination: any) =>
    fetchRaBillsPayableDrillDown(
      raBillsPayableSiteId,
      newPagination.current,
      newPagination.pageSize
    );

  // RA Bills Aging drill-down
  const [raBillsAgingSummary, setRaBillsAgingSummary] = useState<any>(null);
  const [raBillsAgingList, setRaBillsAgingList] = useState<any[]>([]);
  const [raBillsAgingLoading, setRaBillsAgingLoading] = useState(false);
  const [raBillsAgingSiteId, setRaBillsAgingSiteId] = useState<any>(null);
  const [raBillsAgingPagination, setRaBillsAgingPagination] = useState(
    buildDefaultPagination()
  );

  const fetchRaBillsAgingDrillDown = async (
    siteId: any,
    page = 1,
    pageSize = raBillsAgingPagination.pageSize
  ) => {
    setRaBillsAgingLoading(true);
    setRaBillsAgingSiteId(siteId);
    try {
      const params = { siteIds: siteId ? String(siteId) : undefined };
      const [sumRes, detRes]: any[] = await Promise.all([
        dashboardPMSApis.fetchRaBillsAgingSummary(params),
        dashboardPMSApis.fetchRaBillsAgingDetails({
          ...params,
          page,
          pageSize,
        }),
      ]);
      setRaBillsAgingSummary(extractDashboardRows(sumRes)[0] || null);
      setRaBillsAgingPagination((prev) => ({
        ...prev,
        current: page,
        pageSize,
        total: extractDashboardTotal(detRes),
      }));
      setRaBillsAgingList(
        extractDashboardRows(detRes).map((row: any, idx: number) => ({
          key: `raba-${row.raBillId ?? idx}`,
          ...row,
        }))
      );
    } catch (error) {
      console.log("fetchRaBillsAgingDrillDown error:", error);
      setRaBillsAgingSummary(null);
      setRaBillsAgingList([]);
    } finally {
      setRaBillsAgingLoading(false);
    }
  };

  const handleRaBillsAgingTableChange = (newPagination: any) =>
    fetchRaBillsAgingDrillDown(
      raBillsAgingSiteId,
      newPagination.current,
      newPagination.pageSize
    );

  // ----------------------------- Budget Analytics -----------------------------
  const [budgetSummaryData, setBudgetSummaryData] = useState<any[]>([]);
  const [budgetLoading, setBudgetLoading] = useState(false);

  const [budgetPagination, setBudgetPagination] = useState(buildSectionPagination());

  const fetchBudgetSummary = async (
    page = budgetPagination.current,
    pageSize = budgetPagination.pageSize
  ) => {
    setBudgetLoading(true);
    try {
      // budget-summary is org-scoped — no filters per doc §6.1.
      const res: any = await dashboardPMSApis.fetchBudgetSummary({
        page,
        pageSize,
      });
      const total = extractDashboardTotal(res);
      setBudgetPagination((prev) => ({
        ...prev,
        current: page,
        pageSize,
        total,
      }));
      const rows = extractDashboardRows(res).map((row: any, idx: number) => {
        // Prefer API's budgetVariance; fall back to availableBudget if missing.
        const varianceRaw = Number(
          row.budgetVariance ?? row.availableBudget ?? 0
        );
        return {
          key: String(row.projectId ?? idx),
          project: row.projectName ?? row.project ?? "-",
          budget: formatExecCurrency(row.totalBudget),
          committed: formatExecCurrency(row.totalCommitted),
          actual: formatExecCurrency(row.totalConsumed),
          utilization: formatExecPercent(row.consumedPct),
          variance: formatExecCurrency(varianceRaw),
          varianceRaw,
          budgetHealth: row.budgetHealth,
          orgId: row.orgId,
          siteId: row.siteId,
          projectId: row.projectId,
        };
      });
      setBudgetSummaryData(rows);
    } catch (error) {
      console.log("fetchBudgetSummary error:", error);
      setBudgetSummaryData([]);
    } finally {
      setBudgetLoading(false);
    }
  };

  const handleBudgetTableChange = (newPagination: any) => {
    fetchBudgetSummary(newPagination.current, newPagination.pageSize);
  };

  // Budget Variance drill-down
  const [budgetVarianceSummary, setBudgetVarianceSummary] = useState<any>(null);
  const [budgetMomRows, setBudgetMomRows] = useState<any[]>([]);
  const [budgetVarianceList, setBudgetVarianceList] = useState<any[]>([]);
  const [budgetVarianceLoading, setBudgetVarianceLoading] = useState(false);

  const formatVariancePct = (pct: any): string => {
    if (pct === null || pct === undefined || pct === "") return "0%";
    const num = typeof pct === "number" ? pct : Number(pct);
    if (Number.isNaN(num)) return String(pct);
    const sign = num > 0 ? "+" : "";
    return `${sign}${num}%`;
  };

  const [budgetVarianceSiteId, setBudgetVarianceSiteId] = useState<any>(null);
  const [budgetVariancePagination, setBudgetVariancePagination] = useState(
    buildDefaultPagination()
  );

  const fetchBudgetVarianceDrillDown = async (
    siteId: any,
    page = 1,
    pageSize = budgetVariancePagination.pageSize
  ) => {
    setBudgetVarianceLoading(true);
    setBudgetVarianceSiteId(siteId);
    try {
      const params = {
        siteIds: siteId ? String(siteId) : undefined,
        page,
        pageSize,
      };
      const [sumRes, detRes]: any[] = await Promise.all([
        dashboardPMSApis.fetchBudgetVarianceSummary(params),
        dashboardPMSApis.fetchBudgetVarianceDetails(params),
      ]);

      // budget-variance-summary returns multiple rows ordered by |variancePct|.
      const sumRows = extractDashboardRows(sumRes);
      setBudgetVarianceSummary(sumRows[0] || null);
      setBudgetMomRows(
        sumRows.map((row: any, idx: number) => ({
          key: `bvs-${idx}`,
          orgId: row.orgId,
          siteId: row.siteId,
          projectId: row.projectId,
          month: `Line ${idx + 1}`,
          plannedBudget: Number(row.planned ?? 0),
          actualBudget: Number(row.actual ?? 0),
          variance: Number(row.variance ?? 0),
          variancePercent: formatVariancePct(row.variancePct),
        }))
      );

      setBudgetVariancePagination((prev) => ({
        ...prev,
        current: page,
        pageSize,
        total: extractDashboardTotal(detRes),
      }));
      setBudgetVarianceList(
        extractDashboardRows(detRes).map((row: any, idx: number) => ({
          key: `bvd-${idx}`,
          category: row.budgetCategory,
          planned: row.planned,
          actual: row.actual,
          variance: row.variance,
          variancePercent: formatVariancePct(row.variancePct),
          impact: row.impact,
        }))
      );
    } catch (error) {
      console.log("fetchBudgetVarianceDrillDown error:", error);
      setBudgetVarianceSummary(null);
      setBudgetMomRows([]);
      setBudgetVarianceList([]);
    } finally {
      setBudgetVarianceLoading(false);
    }
  };

  const handleBudgetVarianceTableChange = (newPagination: any) =>
    fetchBudgetVarianceDrillDown(
      budgetVarianceSiteId,
      newPagination.current,
      newPagination.pageSize
    );

  useEffect(() => {
    fetchExecutiveSummary(1, 10);
    fetchProjectAnalyticsGraph();
    fetchProjectAnalyticsTabular(1, 10);
    fetchProcurementPayablesGraph();
    fetchProcurementPayablesTabular(1, 10);
    fetchInventorySummary(1, 10);
    fetchWoRaBillsGraph();
    fetchWoRaBillsTabular(1, 10);
    fetchBudgetSummary(1, 10);
  }, []);

  // Executive Summary Columns
  const executiveSummaryColumns = [
    {
      title: "Project Name",
      dataIndex: "projectName",
      key: "projectName",
      fixed: "left" as const,
      width: 150,
      render: (text: string) => (
        <span style={{ fontWeight: 600, color: "#1890ff" }}>{text}</span>
      ),
    },
    {
      title: "Current Progress",
      dataIndex: "currentProgress",
      key: "currentProgress",
      width: 180,
      render: (progress: string) => {
        const value = parseInt(progress);
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Progress
              percent={value}
              size="small"
              strokeColor={{
                '0%': '#64B5F6',
                '100%': '#4DB6AC',
              }}
              style={{ width: '100px' }}
            />
            {/* <span style={{ fontWeight: 600, minWidth: '40px' }}>{progress}</span> */}
          </div>
        );
      },
    },
    {
      title: "Budget Value",
      dataIndex: "budgetValue",
      key: "budgetValue",
      width: 150,
    },
    {
      title: "Budget Utilization",
      dataIndex: "budgetUtilization",
      key: "budgetUtilization",
      width: 170,
    },
    {
      title: "PO Value",
      dataIndex: "poValue",
      key: "poValue",
      width: 150,
    },
    {
      title: "Invoice Payables",
      dataIndex: "invoicePayables",
      key: "invoicePayables",
      width: 160,
    },
    {
      title: "WO Value",
      dataIndex: "woValue",
      key: "woValue",
      width: 150,
    },
    {
      title: "RA Bills Payable",
      dataIndex: "raBillsPayable",
      key: "raBillsPayable",
      width: 160,
    },
  ];

  // Project Management Analytics Chart
  const projectManagementChartOptions = {
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "shadow" },
    },
    legend: {
      data: ["Planned", "Actual Progress", "Avg Delay Days"],
      bottom: 0,
      textStyle: {
        fontSize: 12,
        fontWeight: 500,
      },
    },
    grid: {
      left: "3%",
      right: "4%",
      bottom: "15%",
      containLabel: true,
    },
    xAxis: {
      type: "category",
      data: projectAnalyticsGraphData.map(
        (r: any) => r.project ?? r.projectName ?? "-"
      ),
      axisLabel: {
        fontSize: 11,
        fontWeight: 500,
      },
    },
    yAxis: [
      {
        type: "value",
        name: "Progress %",
        max: 100,
        axisLabel: {
          fontSize: 11,
        },
      },
      {
        type: "value",
        name: "Delay Days",
        axisLabel: {
          fontSize: 11,
        },
      },
    ],
    series: [
      {
        name: "Planned",
        type: "bar",
        data: projectAnalyticsGraphData.map((r: any) => r.planned ?? 0),
        barWidth: "20%",
        itemStyle: {
          color: "#BBDEFB",
          borderColor: "#42A5F5",
          borderWidth: 1,
          borderRadius: [4, 4, 0, 0],
        },
        emphasis: {
          itemStyle: {
            color: "#90CAF9",
            borderColor: "#1E88E5",
            borderWidth: 2,
          },
        },
      },
      {
        name: "Actual Progress",
        type: "bar",
        data: projectAnalyticsGraphData.map(
          (r: any) => r.actualProgress ?? 0
        ),
        barWidth: "20%",
        itemStyle: {
          color: "#B2DFDB",
          borderColor: "#26A69A",
          borderWidth: 1,
          borderRadius: [4, 4, 0, 0],
        },
        emphasis: {
          itemStyle: {
            color: "#80CBC4",
            borderColor: "#00897B",
            borderWidth: 2,
          },
        },
      },
      {
        name: "Avg Delay Days",
        type: "line",
        yAxisIndex: 1,
        data: projectAnalyticsGraphData.map((r: any) => r.avgDelayDays ?? 0),
        lineStyle: {
          width: 3,
          color: "#FFB74D",
        },
        itemStyle: {
          color: "#FFB74D",
          borderWidth: 2,
          borderColor: "#fff",
        },
        symbol: "circle",
        symbolSize: 8,
        smooth: true,
      },
    ],
  };

  // Project Management Analytics Table Data
  const [projectMgmtTableData] = useState([
    {
      key: "1",
      projectName: "Grandeur Heights",
      actualProgress: "45%",
      avgDelayDays: 5,
      overallStatus: "Delayed",
      type: "project",
      children: [
        {
          key: "1-1",
          projectName: "Foundation & Structural Work",
          actualProgress: "65%",
          avgDelayDays: 3,
          overallStatus: "On Track",
          type: "phase",
          children: [
            {
              key: "1-1-1",
              projectName: "Site Preparation",
              actualProgress: "100%",
              avgDelayDays: 0,
              overallStatus: "On Track",
              type: "activity",
              children: [
                {
                  key: "1-1-1-1",
                  projectName: "Land Surveying",
                  actualProgress: "100%",
                  avgDelayDays: 0,
                  overallStatus: "On Track",
                  type: "task",
                },
                {
                  key: "1-1-1-2",
                  projectName: "Soil Testing",
                  actualProgress: "100%",
                  avgDelayDays: 0,
                  overallStatus: "On Track",
                  type: "task",
                },
              ],
            },
            {
              key: "1-1-2",
              projectName: "Foundation Excavation",
              actualProgress: "80%",
              avgDelayDays: 2,
              overallStatus: "On Track",
              type: "activity",
              children: [
                {
                  key: "1-1-2-1",
                  projectName: "Excavation Work",
                  actualProgress: "100%",
                  avgDelayDays: 0,
                  overallStatus: "On Track",
                  type: "task",
                },
                {
                  key: "1-1-2-2",
                  projectName: "Foundation Laying",
                  actualProgress: "60%",
                  avgDelayDays: 4,
                  overallStatus: "Delayed",
                  type: "task",
                },
              ],
            },
          ],
        },
        {
          key: "1-2",
          projectName: "MEP Installation",
          actualProgress: "35%",
          avgDelayDays: 8,
          overallStatus: "Delayed",
          type: "phase",
          children: [
            {
              key: "1-2-1",
              projectName: "Electrical Works",
              actualProgress: "40%",
              avgDelayDays: 6,
              overallStatus: "Delayed",
              type: "activity",
              children: [
                {
                  key: "1-2-1-1",
                  projectName: "Cable Tray Installation",
                  actualProgress: "50%",
                  avgDelayDays: 5,
                  overallStatus: "Delayed",
                  type: "task",
                },
                {
                  key: "1-2-1-2",
                  projectName: "Panel Board Setup",
                  actualProgress: "30%",
                  avgDelayDays: 7,
                  overallStatus: "Delayed",
                  type: "task",
                },
              ],
            },
          ],
        },
      ],
    },
    {
      key: "2",
      projectName: "Paramount Rise",
      actualProgress: "72%",
      avgDelayDays: 3,
      overallStatus: "On Track",
      type: "project",
      children: [
        {
          key: "2-1",
          projectName: "Civil Construction",
          actualProgress: "85%",
          avgDelayDays: 2,
          overallStatus: "On Track",
          type: "phase",
          children: [
            {
              key: "2-1-1",
              projectName: "Masonry Work",
              actualProgress: "90%",
              avgDelayDays: 1,
              overallStatus: "On Track",
              type: "activity",
              children: [
                {
                  key: "2-1-1-1",
                  projectName: "Brick Work - Ground Floor",
                  actualProgress: "100%",
                  avgDelayDays: 0,
                  overallStatus: "On Track",
                  type: "task",
                },
                {
                  key: "2-1-1-2",
                  projectName: "Brick Work - First Floor",
                  actualProgress: "80%",
                  avgDelayDays: 2,
                  overallStatus: "On Track",
                  type: "task",
                },
              ],
            },
          ],
        },
        {
          key: "2-2",
          projectName: "Finishing Works",
          actualProgress: "60%",
          avgDelayDays: 5,
          overallStatus: "Slightly Deviated",
          type: "phase",
          children: [
            {
              key: "2-2-1",
              projectName: "Plastering & Painting",
              actualProgress: "55%",
              avgDelayDays: 6,
              overallStatus: "Slightly Deviated",
              type: "activity",
              children: [
                {
                  key: "2-2-1-1",
                  projectName: "Internal Plastering",
                  actualProgress: "70%",
                  avgDelayDays: 4,
                  overallStatus: "On Track",
                  type: "task",
                },
                {
                  key: "2-2-1-2",
                  projectName: "External Painting",
                  actualProgress: "40%",
                  avgDelayDays: 8,
                  overallStatus: "Delayed",
                  type: "task",
                },
              ],
            },
          ],
        },
      ],
    },
    {
      key: "3",
      projectName: "Elysium Towers",
      actualProgress: "28%",
      avgDelayDays: 12,
      overallStatus: "Deviated",
      type: "project",
      children: [
        {
          key: "3-1",
          projectName: "Design & Planning",
          actualProgress: "50%",
          avgDelayDays: 15,
          overallStatus: "Deviated",
          type: "phase",
          children: [
            {
              key: "3-1-1",
              projectName: "Architectural Design",
              actualProgress: "60%",
              avgDelayDays: 12,
              overallStatus: "Deviated",
              type: "activity",
              children: [
                {
                  key: "3-1-1-1",
                  projectName: "Floor Plan Design",
                  actualProgress: "80%",
                  avgDelayDays: 8,
                  overallStatus: "Delayed",
                  type: "task",
                },
                {
                  key: "3-1-1-2",
                  projectName: "3D Modeling",
                  actualProgress: "40%",
                  avgDelayDays: 16,
                  overallStatus: "Deviated",
                  type: "task",
                },
              ],
            },
          ],
        },
      ],
    },
    {
      key: "4",
      projectName: "Skyline Royale",
      actualProgress: "55%",
      avgDelayDays: 7,
      overallStatus: "Slightly Deviated",
      type: "project",
      children: [
        {
          key: "4-1",
          projectName: "Infrastructure Development",
          actualProgress: "65%",
          avgDelayDays: 5,
          overallStatus: "Slightly Deviated",
          type: "phase",
          children: [
            {
              key: "4-1-1",
              projectName: "Road Construction",
              actualProgress: "70%",
              avgDelayDays: 4,
              overallStatus: "On Track",
              type: "activity",
              children: [
                {
                  key: "4-1-1-1",
                  projectName: "Base Course Laying",
                  actualProgress: "90%",
                  avgDelayDays: 2,
                  overallStatus: "On Track",
                  type: "task",
                },
                {
                  key: "4-1-1-2",
                  projectName: "Asphalt Paving",
                  actualProgress: "50%",
                  avgDelayDays: 6,
                  overallStatus: "Delayed",
                  type: "task",
                },
              ],
            },
          ],
        },
      ],
    },
  ]);

  // Handle project row click
  const handleProjectClick = (record: any) => {
    setSelectedProject(record);
    setDrawerVisible(true);
    if (record?.projectId) {
      fetchProjectDrillDown(record.projectId);
    }
  };

  // True only while the row still holds the placeholder (not yet loaded).
  const hasPlaceholderChild = (node: any) =>
    !!node?.children?.length && node.children[0]?.__placeholder === true;

  // Lazy-load a phase's activities (project-activity-details).
  const togglePhaseExpand = async (phase: any) => {
    if (!phase || !hasPlaceholderChild(phase)) return;
    const activities = await fetchPhaseActivities(
      phase.projectId,
      phase.phaseId
    );
    setSelectedProjectPhases((prev) =>
      prev.map((p) =>
        p.key === phase.key
          ? {
              ...p,
              children: activities.length ? activities : [],
            }
          : p
      )
    );
  };

  // Lazy-load an activity's tasks (project-task-details).
  const toggleActivityExpand = async (phaseKey: string, activity: any) => {
    if (!activity || !hasPlaceholderChild(activity)) return;
    const tasks = await fetchActivityTasks(
      activity.projectId,
      activity.phaseId,
      activity.activityId
    );
    setSelectedProjectPhases((prev) =>
      prev.map((p) =>
        p.key === phaseKey
          ? {
              ...p,
              children: (p.children || []).map((a: any) =>
                a.key === activity.key
                  ? { ...a, children: tasks.length ? tasks : [] }
                  : a
              ),
            }
          : p
      )
    );
  };

  // Project Management Analytics Table Columns (Only Projects)
  const projectMgmtTableColumns = [
    {
      title: "Project Name",
      dataIndex: "projectName",
      key: "projectName",
      render: (text: string) => (
        <span style={{ fontWeight: 600, color: "#1890ff" }}>{text}</span>
      ),
    },
    {
      title: "Actual Progress",
      dataIndex: "actualProgress",
      key: "actualProgress",
      render: (text: string) => {
        const value = parseInt(text);
        const color = value > 70 ? "green" : value > 40 ? "orange" : "red";
        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: "Avg Delay Days",
      dataIndex: "avgDelayDays",
      key: "avgDelayDays",
      render: (days: number) => {
        const color = days > 10 ? "#ff4d4f" : days > 5 ? "#faad14" : "#52c41a";
        return <span style={{ color, fontWeight: 500 }}>{days} days</span>;
      },
    },
    {
      title: "Overall Status",
      dataIndex: "overallStatus",
      key: "overallStatus",
      render: (status: string) => {
        const getStatusConfig = (status: string) => {
          switch (status) {
            case "On Track":
              return { color: "green", text: "On Track (0 delay)" };
            case "Delayed":
              return { color: "orange", text: "Delayed" };
            case "Slightly Deviated":
              return { color: "blue", text: "Slightly Deviated (7-15 days)" };
            case "Deviated":
              return { color: "red", text: "Deviated (>15 days)" };
            default:
              return { color: "default", text: status };
          }
        };
        const config = getStatusConfig(status);
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
  ];

  // Drawer Table Columns for Phases/Activities/Tasks
  const drawerTableColumns = [
    {
      title: "Name",
      dataIndex: "projectName",
      key: "projectName",
      render: (text: string, record: any) => {
        const getIcon = (type: string) => {
          switch (type) {
            case "phase":
              return "📂";
            case "activity":
              return "📄";
            case "task":
              return "📋";
            default:
              return "";
          }
        };
        if (record.__placeholder) {
          return (
            <span style={{ color: "#999", fontStyle: "italic" }}>
              {getIcon(record.type)} {text}
            </span>
          );
        }
        return (
          <span style={{ fontWeight: record.type === "phase" ? 600 : 400 }}>
            {getIcon(record.type)} {text}
          </span>
        );
      },
    },
    {
      title: "Actual Progress",
      dataIndex: "actualProgress",
      key: "actualProgress",
      render: (text: string, record: any) => {
        if (record.__placeholder) return null;
        const value = parseInt(text);
        const color = value > 70 ? "green" : value > 40 ? "orange" : "red";
        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: "Avg Delay Days",
      dataIndex: "avgDelayDays",
      key: "avgDelayDays",
      render: (days: number, record: any) => {
        if (record.__placeholder) return null;
        const color = days > 10 ? "#ff4d4f" : days > 5 ? "#faad14" : "#52c41a";
        return <span style={{ color, fontWeight: 500 }}>{days} days</span>;
      },
    },
    {
      title: "Status",
      dataIndex: "overallStatus",
      key: "overallStatus",
      render: (status: string, record: any) => {
        if (record.__placeholder) return null;
        const getStatusConfig = (status: string) => {
          switch (status) {
            case "On Track":
              return { color: "green", text: "On Track" };
            case "Delayed":
              return { color: "orange", text: "Delayed" };
            case "Slightly Deviated":
              return { color: "blue", text: "Slightly Deviated" };
            case "Deviated":
              return { color: "red", text: "Deviated" };
            default:
              return { color: "default", text: status };
          }
        };
        const config = getStatusConfig(status);
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
  ];

  // Procurement and Payables Chart
  const procurementPayablesChartOptions = {
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "shadow" },
    },
    legend: {
      data: ["PO", "Invoice", "Outstanding", "On-Time %"],
      bottom: 0,
      textStyle: {
        fontSize: 12,
        fontWeight: 500,
      },
    },
    grid: {
      left: "3%",
      right: "4%",
      bottom: "15%",
      containLabel: true,
    },
    xAxis: {
      type: "category",
      data: procurementGraphData.map(
        (r: any) => r.project ?? r.projectName ?? "-"
      ),
      axisLabel: {
        fontSize: 11,
        fontWeight: 500,
      },
    },
    yAxis: [
      {
        type: "value",
        name: "Value (₹ Lakhs)",
        axisLabel: {
          fontSize: 11,
        },
      },
      {
        type: "value",
        name: "Percentage (%)",
        max: 100,
        axisLabel: {
          fontSize: 11,
        },
      },
    ],
    series: [
      {
        name: "PO",
        type: "bar",
        data: procurementGraphData.map((r: any) => r.po ?? 0),
        barWidth: "18%",
        itemStyle: {
          color: "#BBDEFB",
          borderColor: "#42A5F5",
          borderWidth: 1,
          borderRadius: [4, 4, 0, 0],
        },
        emphasis: {
          itemStyle: {
            color: "#90CAF9",
            borderColor: "#1E88E5",
            borderWidth: 2,
          },
        },
      },
      {
        name: "Invoice",
        type: "bar",
        data: procurementGraphData.map((r: any) => r.invoice ?? 0),
        barWidth: "18%",
        itemStyle: {
          color: "#B2DFDB",
          borderColor: "#26A69A",
          borderWidth: 1,
          borderRadius: [4, 4, 0, 0],
        },
        emphasis: {
          itemStyle: {
            color: "#80CBC4",
            borderColor: "#00897B",
            borderWidth: 2,
          },
        },
      },
      {
        name: "Outstanding",
        type: "bar",
        data: procurementGraphData.map((r: any) => r.outstanding ?? 0),
        barWidth: "18%",
        itemStyle: {
          color: "#FFCC80",
          borderColor: "#FF9800",
          borderWidth: 1,
          borderRadius: [4, 4, 0, 0],
        },
        emphasis: {
          itemStyle: {
            color: "#FFB74D",
            borderColor: "#F57C00",
            borderWidth: 2,
          },
        },
      },
      {
        name: "On-Time %",
        type: "line",
        yAxisIndex: 1,
        data: procurementGraphData.map((r: any) => r.onTimePct ?? 0),
        lineStyle: {
          width: 3,
          color: "#9575CD",
        },
        itemStyle: {
          color: "#9575CD",
          borderWidth: 2,
          borderColor: "#fff",
        },
        symbol: "circle",
        symbolSize: 8,
        smooth: true,
      },
    ],
  };

  // Procurement and Payables Table Data with Invoice Details
  const [procurementTableData] = useState([
    {
      key: "1",
      projectName: "Grandeur Heights",
      poCount: 8,
      poValue: "₹15,00,000",
      invoicePayablesCount: 5,
      invoicePayablesValue: "₹8,00,000",
      onTimeValue: "₹12,75,000",
      onTimePercent: "85%",
      outstandingValue: "₹2,25,000",
      outstandingPercent: "15%",
      overdueValue: "₹50,000",
      overduePercent: "3.3%",
      avgAgingDays: 18,
      purchaseOrders: [
        {
          key: "1-1",
          poNumber: "PO-2025-001",
          vendor: "Steel Suppliers Ltd.",
          poDate: "2025-07-01",
          poValue: "₹2,50,000",
          status: "Partially Received",
          receivedValue: "₹2,00,000",
        },
        {
          key: "1-2",
          poNumber: "PO-2025-002",
          vendor: "Cement Trading Co.",
          poDate: "2025-07-05",
          poValue: "₹1,80,000",
          status: "Received",
          receivedValue: "₹1,80,000",
        },
        {
          key: "1-3",
          poNumber: "PO-2025-003",
          vendor: "Hardware Mart",
          poDate: "2025-07-10",
          poValue: "₹1,50,000",
          status: "Received",
          receivedValue: "₹1,50,000",
        },
        {
          key: "1-4",
          poNumber: "PO-2025-004",
          vendor: "Electrical Supplies Inc.",
          poDate: "2025-07-15",
          poValue: "₹2,20,000",
          status: "Partially Received",
          receivedValue: "₹1,50,000",
        },
        {
          key: "1-5",
          poNumber: "PO-2025-005",
          vendor: "Plumbing Materials",
          poDate: "2025-07-20",
          poValue: "₹1,20,000",
          status: "Received",
          receivedValue: "₹1,20,000",
        },
        {
          key: "1-6",
          poNumber: "PO-2025-006",
          vendor: "Paint Suppliers",
          poDate: "2025-07-25",
          poValue: "₹1,80,000",
          status: "Pending",
          receivedValue: "₹0",
        },
        {
          key: "1-7",
          poNumber: "PO-2025-007",
          vendor: "Tiles & Marble",
          poDate: "2025-08-01",
          poValue: "₹2,00,000",
          status: "Partially Received",
          receivedValue: "₹1,00,000",
        },
        {
          key: "1-8",
          poNumber: "PO-2025-008",
          vendor: "Safety Equipment",
          poDate: "2025-08-05",
          poValue: "₹2,00,000",
          status: "Received",
          receivedValue: "₹2,00,000",
        },
      ],
      invoicePayables: [
        {
          key: "1-1",
          invoiceNumber: "INV-2025-001",
          vendor: "Steel Suppliers Ltd.",
          invoiceDate: "2025-07-15",
          dueDate: "2025-08-14",
          amount: "₹2,00,000",
          dueDays: 26,
          status: "Overdue",
        },
        {
          key: "1-2",
          invoiceNumber: "INV-2025-002",
          vendor: "Cement Trading Co.",
          invoiceDate: "2025-07-20",
          dueDate: "2025-08-19",
          amount: "₹1,80,000",
          dueDays: 21,
          status: "Overdue",
        },
        {
          key: "1-3",
          invoiceNumber: "INV-2025-003",
          vendor: "Hardware Mart",
          invoiceDate: "2025-07-25",
          dueDate: "2025-08-24",
          amount: "₹1,50,000",
          dueDays: 16,
          status: "Overdue",
        },
        {
          key: "1-4",
          invoiceNumber: "INV-2025-004",
          vendor: "Electrical Supplies Inc.",
          invoiceDate: "2025-08-01",
          dueDate: "2025-08-31",
          amount: "₹1,50,000",
          dueDays: 9,
          status: "Due Soon",
        },
        {
          key: "1-5",
          invoiceNumber: "INV-2025-005",
          vendor: "Plumbing Materials",
          invoiceDate: "2025-08-05",
          dueDate: "2025-09-04",
          amount: "₹1,20,000",
          dueDays: 5,
          status: "Due Soon",
        },
      ],
      invoices: [
        {
          key: "1-1",
          invoiceNumber: "INV-2025-001",
          vendor: "Steel Suppliers Ltd.",
          invoiceDate: "2025-07-15",
          dueDate: "2025-08-14",
          overdueAmount: "₹15,000",
          agingDays: 26,
        },
        {
          key: "1-2",
          invoiceNumber: "INV-2025-002",
          vendor: "Cement Trading Co.",
          invoiceDate: "2025-07-20",
          dueDate: "2025-08-19",
          overdueAmount: "₹12,000",
          agingDays: 21,
        },
        {
          key: "1-3",
          invoiceNumber: "INV-2025-003",
          vendor: "Hardware Mart",
          invoiceDate: "2025-07-25",
          dueDate: "2025-08-24",
          overdueAmount: "₹8,000",
          agingDays: 16,
        },
        {
          key: "1-4",
          invoiceNumber: "INV-2025-004",
          vendor: "Electrical Supplies Inc.",
          invoiceDate: "2025-08-01",
          dueDate: "2025-08-31",
          overdueAmount: "₹10,000",
          agingDays: 9,
        },
        {
          key: "1-5",
          invoiceNumber: "INV-2025-005",
          vendor: "Plumbing Materials",
          invoiceDate: "2025-08-05",
          dueDate: "2025-09-04",
          overdueAmount: "₹5,000",
          agingDays: 5,
        },
      ],
    },
    {
      key: "2",
      projectName: "Paramount Rise",
      poCount: 10,
      poValue: "₹25,00,000",
      invoicePayablesCount: 7,
      invoicePayablesValue: "₹12,00,000",
      onTimeValue: "₹23,00,000",
      onTimePercent: "92%",
      outstandingValue: "₹2,00,000",
      outstandingPercent: "8%",
      overdueValue: "₹25,000",
      overduePercent: "1%",
      avgAgingDays: 12,
      purchaseOrders: [
        {
          key: "2-1",
          poNumber: "PO-2025-009",
          vendor: "Ready Mix Concrete Ltd.",
          poDate: "2025-07-08",
          poValue: "₹3,00,000",
          status: "Received",
          receivedValue: "₹3,00,000",
        },
        {
          key: "2-2",
          poNumber: "PO-2025-010",
          vendor: "Steel Suppliers",
          poDate: "2025-07-12",
          poValue: "₹2,50,000",
          status: "Received",
          receivedValue: "₹2,50,000",
        },
        {
          key: "2-3",
          poNumber: "PO-2025-011",
          vendor: "Brick Suppliers",
          poDate: "2025-07-18",
          poValue: "₹2,00,000",
          status: "Partially Received",
          receivedValue: "₹1,50,000",
        },
        {
          key: "2-4",
          poNumber: "PO-2025-012",
          vendor: "Sand & Aggregates",
          poDate: "2025-07-22",
          poValue: "₹1,80,000",
          status: "Received",
          receivedValue: "₹1,80,000",
        },
        {
          key: "2-5",
          poNumber: "PO-2025-013",
          vendor: "Electrical Materials",
          poDate: "2025-07-28",
          poValue: "₹2,20,000",
          status: "Received",
          receivedValue: "₹2,20,000",
        },
        {
          key: "2-6",
          poNumber: "PO-2025-014",
          vendor: "Plumbing Items",
          poDate: "2025-08-02",
          poValue: "₹1,50,000",
          status: "Partially Received",
          receivedValue: "₹1,00,000",
        },
        {
          key: "2-7",
          poNumber: "PO-2025-015",
          vendor: "Paint & Finishing",
          poDate: "2025-08-06",
          poValue: "₹3,00,000",
          status: "Received",
          receivedValue: "₹3,00,000",
        },
        {
          key: "2-8",
          poNumber: "PO-2025-016",
          vendor: "Tiles & Flooring",
          poDate: "2025-08-10",
          poValue: "₹2,80,000",
          status: "Received",
          receivedValue: "₹2,80,000",
        },
        {
          key: "2-9",
          poNumber: "PO-2025-017",
          vendor: "Doors & Windows",
          poDate: "2025-08-14",
          poValue: "₹3,20,000",
          status: "Pending",
          receivedValue: "₹0",
        },
        {
          key: "2-10",
          poNumber: "PO-2025-018",
          vendor: "HVAC Equipment",
          poDate: "2025-08-18",
          poValue: "₹3,00,000",
          status: "Partially Received",
          receivedValue: "₹1,50,000",
        },
      ],
      invoicePayables: [
        {
          key: "2-1",
          invoiceNumber: "INV-2025-006",
          vendor: "Ready Mix Concrete Ltd.",
          invoiceDate: "2025-08-10",
          dueDate: "2025-09-09",
          amount: "₹3,00,000",
          dueDays: 15,
          status: "Due Soon",
        },
        {
          key: "2-2",
          invoiceNumber: "INV-2025-007",
          vendor: "Steel Suppliers",
          invoiceDate: "2025-08-12",
          dueDate: "2025-09-11",
          amount: "₹2,50,000",
          dueDays: 13,
          status: "Due Soon",
        },
        {
          key: "2-3",
          invoiceNumber: "INV-2025-008",
          vendor: "Brick Suppliers",
          invoiceDate: "2025-08-14",
          dueDate: "2025-09-13",
          amount: "₹1,50,000",
          dueDays: 11,
          status: "Due Soon",
        },
        {
          key: "2-4",
          invoiceNumber: "INV-2025-009",
          vendor: "Sand & Aggregates",
          invoiceDate: "2025-08-15",
          dueDate: "2025-09-14",
          amount: "₹1,80,000",
          dueDays: 10,
          status: "Due Soon",
        },
        {
          key: "2-5",
          invoiceNumber: "INV-2025-010",
          vendor: "Electrical Materials",
          invoiceDate: "2025-08-16",
          dueDate: "2025-09-15",
          amount: "₹2,20,000",
          dueDays: 9,
          status: "Upcoming",
        },
        {
          key: "2-6",
          invoiceNumber: "INV-2025-011",
          vendor: "Paint & Finishing",
          invoiceDate: "2025-08-18",
          dueDate: "2025-09-17",
          amount: "₹3,00,000",
          dueDays: 7,
          status: "Upcoming",
        },
        {
          key: "2-7",
          invoiceNumber: "INV-2025-012",
          vendor: "Tiles & Flooring",
          invoiceDate: "2025-08-20",
          dueDate: "2025-09-19",
          amount: "₹2,80,000",
          dueDays: 5,
          status: "Upcoming",
        },
      ],
      invoices: [
        {
          key: "2-1",
          invoiceNumber: "INV-2025-006",
          vendor: "Ready Mix Concrete Ltd.",
          invoiceDate: "2025-08-10",
          dueDate: "2025-09-09",
          overdueAmount: "₹15,000",
          agingDays: 15,
        },
        {
          key: "2-2",
          invoiceNumber: "INV-2025-007",
          vendor: "Paint Suppliers",
          invoiceDate: "2025-08-15",
          dueDate: "2025-09-14",
          overdueAmount: "₹10,000",
          agingDays: 10,
        },
      ],
    },
    {
      key: "3",
      projectName: "Elysium Towers",
      poCount: 6,
      poValue: "₹10,00,000",
      invoicePayablesCount: 4,
      invoicePayablesValue: "₹4,00,000",
      onTimeValue: "₹7,80,000",
      onTimePercent: "78%",
      outstandingValue: "₹2,20,000",
      outstandingPercent: "22%",
      overdueValue: "₹80,000",
      overduePercent: "8%",
      avgAgingDays: 25,
      purchaseOrders: [
        {
          key: "3-1",
          poNumber: "PO-2025-019",
          vendor: "Granite Works",
          poDate: "2025-07-05",
          poValue: "₹2,00,000",
          status: "Received",
          receivedValue: "₹2,00,000",
        },
        {
          key: "3-2",
          poNumber: "PO-2025-020",
          vendor: "Tile Merchants",
          poDate: "2025-07-10",
          poValue: "₹1,50,000",
          status: "Partially Received",
          receivedValue: "₹1,00,000",
        },
        {
          key: "3-3",
          poNumber: "PO-2025-021",
          vendor: "Aluminum Fabricators",
          poDate: "2025-07-15",
          poValue: "₹1,80,000",
          status: "Received",
          receivedValue: "₹1,80,000",
        },
        {
          key: "3-4",
          poNumber: "PO-2025-022",
          vendor: "Glass & Glazing Co.",
          poDate: "2025-07-20",
          poValue: "₹2,20,000",
          status: "Partially Received",
          receivedValue: "₹1,50,000",
        },
        {
          key: "3-5",
          poNumber: "PO-2025-023",
          vendor: "Sanitary Ware Depot",
          poDate: "2025-07-25",
          poValue: "₹1,20,000",
          status: "Received",
          receivedValue: "₹1,20,000",
        },
        {
          key: "3-6",
          poNumber: "PO-2025-024",
          vendor: "Wooden Flooring",
          poDate: "2025-08-01",
          poValue: "₹1,30,000",
          status: "Pending",
          receivedValue: "₹0",
        },
      ],
      invoicePayables: [
        {
          key: "3-1",
          invoiceNumber: "INV-2025-008",
          vendor: "Granite Works",
          invoiceDate: "2025-07-05",
          dueDate: "2025-08-04",
          amount: "₹2,00,000",
          dueDays: 35,
          status: "Overdue",
        },
        {
          key: "3-2",
          invoiceNumber: "INV-2025-009",
          vendor: "Tile Merchants",
          invoiceDate: "2025-07-10",
          dueDate: "2025-08-09",
          amount: "₹1,00,000",
          dueDays: 30,
          status: "Overdue",
        },
        {
          key: "3-3",
          invoiceNumber: "INV-2025-010",
          vendor: "Aluminum Fabricators",
          invoiceDate: "2025-07-15",
          dueDate: "2025-08-14",
          amount: "₹1,80,000",
          dueDays: 26,
          status: "Overdue",
        },
        {
          key: "3-4",
          invoiceNumber: "INV-2025-011",
          vendor: "Glass & Glazing Co.",
          invoiceDate: "2025-07-25",
          dueDate: "2025-08-24",
          amount: "₹1,50,000",
          dueDays: 16,
          status: "Overdue",
        },
      ],
      invoices: [
        {
          key: "3-1",
          invoiceNumber: "INV-2025-008",
          vendor: "Granite Works",
          invoiceDate: "2025-07-05",
          dueDate: "2025-08-04",
          overdueAmount: "₹25,000",
          agingDays: 35,
        },
        {
          key: "3-2",
          invoiceNumber: "INV-2025-009",
          vendor: "Tile Merchants",
          invoiceDate: "2025-07-10",
          dueDate: "2025-08-09",
          overdueAmount: "₹20,000",
          agingDays: 30,
        },
        {
          key: "3-3",
          invoiceNumber: "INV-2025-010",
          vendor: "Aluminum Fabricators",
          invoiceDate: "2025-07-15",
          dueDate: "2025-08-14",
          overdueAmount: "₹18,000",
          agingDays: 26,
        },
        {
          key: "3-4",
          invoiceNumber: "INV-2025-011",
          vendor: "Glass & Glazing Co.",
          invoiceDate: "2025-07-25",
          dueDate: "2025-08-24",
          overdueAmount: "₹12,000",
          agingDays: 16,
        },
        {
          key: "3-5",
          invoiceNumber: "INV-2025-012",
          vendor: "Sanitary Ware Depot",
          invoiceDate: "2025-08-01",
          dueDate: "2025-08-31",
          overdueAmount: "₹5,000",
          agingDays: 9,
        },
      ],
    },
    {
      key: "4",
      projectName: "Skyline Royale",
      poCount: 9,
      poValue: "₹18,00,000",
      invoicePayablesCount: 6,
      invoicePayablesValue: "₹10,00,000",
      onTimeValue: "₹15,84,000",
      onTimePercent: "88%",
      outstandingValue: "₹2,16,000",
      outstandingPercent: "12%",
      overdueValue: "₹45,000",
      overduePercent: "2.5%",
      avgAgingDays: 15,
      purchaseOrders: [
        {
          key: "4-1",
          poNumber: "PO-2025-025",
          vendor: "Wood & Timber Co.",
          poDate: "2025-08-01",
          poValue: "₹2,50,000",
          status: "Received",
          receivedValue: "₹2,50,000",
        },
        {
          key: "4-2",
          poNumber: "PO-2025-026",
          vendor: "Roofing Materials",
          poDate: "2025-08-05",
          poValue: "₹2,00,000",
          status: "Received",
          receivedValue: "₹2,00,000",
        },
        {
          key: "4-3",
          poNumber: "PO-2025-027",
          vendor: "Safety Equipment Ltd.",
          poDate: "2025-08-08",
          poValue: "₹1,50,000",
          status: "Partially Received",
          receivedValue: "₹1,00,000",
        },
        {
          key: "4-4",
          poNumber: "PO-2025-028",
          vendor: "Cement & Concrete",
          poDate: "2025-08-10",
          poValue: "₹2,20,000",
          status: "Received",
          receivedValue: "₹2,20,000",
        },
        {
          key: "4-5",
          poNumber: "PO-2025-029",
          vendor: "Steel Bars",
          poDate: "2025-08-12",
          poValue: "₹2,80,000",
          status: "Received",
          receivedValue: "₹2,80,000",
        },
        {
          key: "4-6",
          poNumber: "PO-2025-030",
          vendor: "Electrical Wiring",
          poDate: "2025-08-14",
          poValue: "₹1,80,000",
          status: "Partially Received",
          receivedValue: "₹1,20,000",
        },
        {
          key: "4-7",
          poNumber: "PO-2025-031",
          vendor: "Plumbing Pipes",
          poDate: "2025-08-16",
          poValue: "₹1,60,000",
          status: "Received",
          receivedValue: "₹1,60,000",
        },
        {
          key: "4-8",
          poNumber: "PO-2025-032",
          vendor: "Paint Materials",
          poDate: "2025-08-18",
          poValue: "₹1,80,000",
          status: "Pending",
          receivedValue: "₹0",
        },
        {
          key: "4-9",
          poNumber: "PO-2025-033",
          vendor: "Fixtures & Fittings",
          poDate: "2025-08-20",
          poValue: "₹1,80,000",
          status: "Partially Received",
          receivedValue: "₹1,00,000",
        },
      ],
      invoicePayables: [
        {
          key: "4-1",
          invoiceNumber: "INV-2025-013",
          vendor: "Wood & Timber Co.",
          invoiceDate: "2025-08-05",
          dueDate: "2025-09-04",
          amount: "₹2,50,000",
          dueDays: 18,
          status: "Due Soon",
        },
        {
          key: "4-2",
          invoiceNumber: "INV-2025-014",
          vendor: "Roofing Materials",
          invoiceDate: "2025-08-10",
          dueDate: "2025-09-09",
          amount: "₹2,00,000",
          dueDays: 13,
          status: "Due Soon",
        },
        {
          key: "4-3",
          invoiceNumber: "INV-2025-015",
          vendor: "Safety Equipment Ltd.",
          invoiceDate: "2025-08-12",
          dueDate: "2025-09-11",
          amount: "₹1,00,000",
          dueDays: 12,
          status: "Due Soon",
        },
        {
          key: "4-4",
          invoiceNumber: "INV-2025-016",
          vendor: "Cement & Concrete",
          invoiceDate: "2025-08-14",
          dueDate: "2025-09-13",
          amount: "₹2,20,000",
          dueDays: 10,
          status: "Upcoming",
        },
        {
          key: "4-5",
          invoiceNumber: "INV-2025-017",
          vendor: "Steel Bars",
          invoiceDate: "2025-08-16",
          dueDate: "2025-09-15",
          amount: "₹2,80,000",
          dueDays: 8,
          status: "Upcoming",
        },
        {
          key: "4-6",
          invoiceNumber: "INV-2025-018",
          vendor: "Plumbing Pipes",
          invoiceDate: "2025-08-18",
          dueDate: "2025-09-17",
          amount: "₹1,60,000",
          dueDays: 6,
          status: "Upcoming",
        },
      ],
      invoices: [
        {
          key: "4-1",
          invoiceNumber: "INV-2025-013",
          vendor: "Wood & Timber Co.",
          invoiceDate: "2025-08-05",
          dueDate: "2025-09-04",
          overdueAmount: "₹20,000",
          agingDays: 18,
        },
        {
          key: "4-2",
          invoiceNumber: "INV-2025-014",
          vendor: "Roofing Materials",
          invoiceDate: "2025-08-10",
          dueDate: "2025-09-09",
          overdueAmount: "₹15,000",
          agingDays: 13,
        },
        {
          key: "4-3",
          invoiceNumber: "INV-2025-015",
          vendor: "Safety Equipment Ltd.",
          invoiceDate: "2025-08-15",
          dueDate: "2025-09-14",
          overdueAmount: "₹10,000",
          agingDays: 12,
        },
      ],
    },
  ]);

  // Handle Avg Aging Days click → invoice-aging drill-down
  const handleAgingDaysClick = (record: any) => {
    setSelectedProcurementProject(record);
    setProcurementDrawerVisible(true);
    fetchInvoiceAgingDrillDown(record?.siteId);
  };

  // Handle PO click → po-summary + po-details
  const handlePoClick = (record: any) => {
    setSelectedPoProject(record);
    setPoDrawerVisible(true);
    fetchPoDrillDown(record?.siteId);
  };

  // Handle Invoice Payables click → invoice-summary + invoice-details
  const handleInvoicePayablesClick = (record: any) => {
    setSelectedInvoicePayablesProject(record);
    setInvoicePayablesDrawerVisible(true);
    fetchInvoiceDrillDown(record?.siteId);
  };

  // Procurement and Payables Table Columns
  const procurementTableColumns = [
    {
      title: "Project Name",
      dataIndex: "projectName",
      key: "projectName",
      fixed: "left" as const,
      width: 150,
      render: (text: string) => (
        <span style={{ fontWeight: 600, color: "#1890ff" }}>{text}</span>
      ),
    },
    {
      title: "PO",
      key: "po",
      children: [
        {
          title: "Count",
          dataIndex: "poCount",
          key: "poCount",
          width: 90,
          render: (count: number, record: any) => (
            <a
              onClick={() => handlePoClick(record)}
              style={{
                cursor: "pointer",
                color: "#1890ff",
                fontWeight: 600,
                textDecoration: "underline",
              }}
            >
              {count} POs
            </a>
          ),
        },
        {
          title: "Value",
          dataIndex: "poValue",
          key: "poValue",
          width: 130,
          render: (text: string) => (
            <span style={{ color: "#1890ff", fontWeight: 500 }}>{text}</span>
          ),
        },
      ],
    },
    {
      title: "Invoice Payables",
      key: "invoicePayables",
      children: [
        {
          title: "Count",
          dataIndex: "invoicePayablesCount",
          key: "invoicePayablesCount",
          width: 90,
          render: (count: number, record: any) => (
            <a
              onClick={() => handleInvoicePayablesClick(record)}
              style={{
                cursor: "pointer",
                color: "#722ed1",
                fontWeight: 600,
                textDecoration: "underline",
              }}
            >
              {count} Invoices
            </a>
          ),
        },
        {
          title: "Value",
          dataIndex: "invoicePayablesValue",
          key: "invoicePayablesValue",
          width: 130,
          render: (text: string) => (
            <span style={{ color: "#722ed1", fontWeight: 500 }}>{text}</span>
          ),
        },
      ],
    },
    {
      title: "On-Time",
      key: "onTime",
      children: [
        {
          title: "Value",
          dataIndex: "onTimeValue",
          key: "onTimeValue",
          width: 130,
          render: (text: string) => (
            <span style={{ color: "#52c41a", fontWeight: 500 }}>{text}</span>
          ),
        },
        {
          title: "%",
          dataIndex: "onTimePercent",
          key: "onTimePercent",
          width: 80,
          render: (text: string) => <Tag color="green">{text}</Tag>,
        },
      ],
    },
    {
      title: "Outstanding",
      key: "outstanding",
      children: [
        {
          title: "Value",
          dataIndex: "outstandingValue",
          key: "outstandingValue",
          width: 130,
          render: (text: string) => (
            <span style={{ color: "#faad14", fontWeight: 500 }}>{text}</span>
          ),
        },
        {
          title: "%",
          dataIndex: "outstandingPercent",
          key: "outstandingPercent",
          width: 80,
          render: (text: string) => <Tag color="orange">{text}</Tag>,
        },
      ],
    },
    {
      title: "Overdue",
      key: "overdue",
      children: [
        {
          title: "Value",
          dataIndex: "overdueValue",
          key: "overdueValue",
          width: 130,
          render: (text: string) => (
            <span style={{ color: "#ff4d4f", fontWeight: 500 }}>{text}</span>
          ),
        },
        {
          title: "%",
          dataIndex: "overduePercent",
          key: "overduePercent",
          width: 80,
          render: (text: string) => <Tag color="red">{text}</Tag>,
        },
      ],
    },
    {
      title: "Avg Aging Days",
      dataIndex: "avgAgingDays",
      key: "avgAgingDays",
      width: 130,
      render: (days: number, record: any) => (
        <a
          onClick={() => handleAgingDaysClick(record)}
          style={{ cursor: "pointer" }}
        >
          <div
            style={{
              fontWeight: 600,
              fontSize: "13px",
              cursor: "pointer",
              textDecoration: "underline",
              color: `${days > 20 ? "red" : days > 15 ? "orange" : "blue"}`,
            }}
          >
            {days} days
          </div>
        </a>
      ),
    },
  ];

  // Work Order & RA Bills Chart
  const workOrderChartOptions = {
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "shadow" },
    },
    legend: {
      data: ["WO Value", "RA Bill", "Outstanding", "Avg Aging Days"],
      bottom: 0,
      textStyle: {
        fontSize: 12,
        fontWeight: 500,
      },
    },
    grid: {
      left: "3%",
      right: "4%",
      bottom: "15%",
      containLabel: true,
    },
    xAxis: {
      type: "category",
      data: woRaGraphData.map((r: any) => r.project ?? r.projectName ?? "-"),
      axisLabel: {
        fontSize: 11,
        fontWeight: 500,
      },
    },
    yAxis: [
      {
        type: "value",
        name: "Value (₹ Lakhs)",
        axisLabel: {
          fontSize: 11,
        },
      },
      {
        type: "value",
        name: "Avg Aging (Days)",
        axisLabel: {
          fontSize: 11,
        },
      },
    ],
    series: [
      {
        name: "WO Value",
        type: "bar",
        data: woRaGraphData.map((r: any) => r.woValue ?? 0),
        barWidth: "18%",
        itemStyle: {
          color: "#BBDEFB",
          borderColor: "#42A5F5",
          borderWidth: 1,
          borderRadius: [4, 4, 0, 0],
        },
        emphasis: {
          itemStyle: {
            color: "#90CAF9",
            borderColor: "#1E88E5",
            borderWidth: 2,
          },
        },
      },
      {
        name: "RA Bill",
        type: "bar",
        data: woRaGraphData.map((r: any) => r.raBill ?? 0),
        barWidth: "18%",
        itemStyle: {
          color: "#B2DFDB",
          borderColor: "#26A69A",
          borderWidth: 1,
          borderRadius: [4, 4, 0, 0],
        },
        emphasis: {
          itemStyle: {
            color: "#80CBC4",
            borderColor: "#00897B",
            borderWidth: 2,
          },
        },
      },
      {
        name: "Outstanding",
        type: "bar",
        data: woRaGraphData.map((r: any) => r.outstanding ?? 0),
        barWidth: "18%",
        itemStyle: {
          color: "#FFCC80",
          borderColor: "#FF9800",
          borderWidth: 1,
          borderRadius: [4, 4, 0, 0],
        },
        emphasis: {
          itemStyle: {
            color: "#FFB74D",
            borderColor: "#F57C00",
            borderWidth: 2,
          },
        },
      },
      {
        name: "Avg Aging Days",
        type: "line",
        yAxisIndex: 1,
        data: woRaGraphData.map((r: any) => r.avgAgingDays ?? 0),
        lineStyle: {
          width: 3,
          color: "#9575CD",
        },
        itemStyle: {
          color: "#9575CD",
          borderWidth: 2,
          borderColor: "#fff",
        },
        symbol: "circle",
        symbolSize: 8,
        smooth: true,
      },
    ],
  };

  // Work Order & RA Bills Table Data with Contractor Details
  const [workOrderTableData] = useState([
    {
      key: "1",
      projectName: "Grandeur Heights",
      woCount: 5,
      woValue: "₹12,00,000",
      executionValue: "₹5,00,000",
      executionPercent: "42%",
      raBillsRaised: "₹5,00,000",
      raBillsApproved: "₹4,00,000",
      raBillsPayable: "₹3,50,000",
      raBillsOutstanding: "₹1,50,000",
      raBillsOverdue: "₹50,000",
      avgAgingDays: 18,
      raBills: [
        {
          key: "1-1",
          raBillNumber: "RAB-2025-001",
          contractor: "L&T Construction Ltd.",
          raisedDate: "2025-08-15",
          dueDate: "2025-09-14",
          amount: "₹80,000",
          overdueAmount: "₹15,000",
          dueDays: 26,
          agingDays: 26,
          status: "Overdue",
        },
        {
          key: "1-2",
          raBillNumber: "RAB-2025-002",
          contractor: "Shapoorji Pallonji & Co.",
          raisedDate: "2025-08-20",
          dueDate: "2025-09-19",
          amount: "₹70,000",
          overdueAmount: "₹10,000",
          dueDays: 21,
          agingDays: 21,
          status: "Overdue",
        },
        {
          key: "1-3",
          raBillNumber: "RAB-2025-003",
          contractor: "Tata Projects Ltd.",
          raisedDate: "2025-08-12",
          dueDate: "2025-09-11",
          amount: "₹90,000",
          overdueAmount: "₹8,000",
          dueDays: 29,
          agingDays: 29,
          status: "Overdue",
        },
        {
          key: "1-4",
          raBillNumber: "RAB-2025-004",
          contractor: "Afcons Infrastructure Ltd.",
          raisedDate: "2025-08-25",
          dueDate: "2025-09-24",
          amount: "₹65,000",
          overdueAmount: "₹12,000",
          dueDays: 16,
          agingDays: 16,
          status: "Overdue",
        },
        {
          key: "1-5",
          raBillNumber: "RAB-2025-005",
          contractor: "NCC Limited",
          raisedDate: "2025-09-01",
          dueDate: "2025-09-30",
          amount: "₹45,000",
          overdueAmount: "₹5,000",
          dueDays: 10,
          agingDays: 10,
          status: "Due Soon",
        },
      ],
      contractors: [
        {
          key: "1-1",
          contractorName: "L&T Construction Ltd.",
          woNumber: "WO-2025-001",
          woValue: "₹3,50,000",
          executionPercent: "45%",
          raBillsRaised: "₹1,50,000",
          raBillsPayable: "₹1,00,000",
          outstanding: "₹50,000",
          overdue: "₹15,000",
          agingDays: 20,
        },
        {
          key: "1-2",
          contractorName: "Shapoorji Pallonji & Co.",
          woNumber: "WO-2025-002",
          woValue: "₹2,80,000",
          executionPercent: "40%",
          raBillsRaised: "₹1,20,000",
          raBillsPayable: "₹90,000",
          outstanding: "₹30,000",
          overdue: "₹10,000",
          agingDays: 15,
        },
        {
          key: "1-3",
          contractorName: "Tata Projects Ltd.",
          woNumber: "WO-2025-003",
          woValue: "₹2,50,000",
          executionPercent: "42%",
          raBillsRaised: "₹1,00,000",
          raBillsPayable: "₹80,000",
          outstanding: "₹20,000",
          overdue: "₹8,000",
          agingDays: 18,
        },
        {
          key: "1-4",
          contractorName: "Afcons Infrastructure Ltd.",
          woNumber: "WO-2025-004",
          woValue: "₹2,00,000",
          executionPercent: "38%",
          raBillsRaised: "₹80,000",
          raBillsPayable: "₹50,000",
          outstanding: "₹30,000",
          overdue: "₹12,000",
          agingDays: 22,
        },
        {
          key: "1-5",
          contractorName: "NCC Limited",
          woNumber: "WO-2025-005",
          woValue: "₹1,20,000",
          executionPercent: "50%",
          raBillsRaised: "₹50,000",
          raBillsPayable: "₹30,000",
          outstanding: "₹20,000",
          overdue: "₹5,000",
          agingDays: 12,
        },
      ],
    },
    {
      key: "2",
      projectName: "Paramount Rise",
      woCount: 7,
      woValue: "₹18,00,000",
      executionValue: "₹7,50,000",
      executionPercent: "42%",
      raBillsRaised: "₹7,50,000",
      raBillsApproved: "₹6,00,000",
      raBillsPayable: "₹5,00,000",
      raBillsOutstanding: "₹2,50,000",
      raBillsOverdue: "₹75,000",
      avgAgingDays: 12,
      raBills: [
        {
          key: "2-1",
          raBillNumber: "RAB-2025-006",
          contractor: "Simplex Infrastructures",
          raisedDate: "2025-09-01",
          dueDate: "2025-09-30",
          overdueAmount: "₹18,000",
          agingDays: 10,
        },
        {
          key: "2-2",
          raBillNumber: "RAB-2025-007",
          contractor: "L&T Construction Ltd.",
          raisedDate: "2025-08-28",
          dueDate: "2025-09-27",
          overdueAmount: "₹15,000",
          agingDays: 13,
        },
        {
          key: "2-3",
          raBillNumber: "RAB-2025-008",
          contractor: "Tata Projects Ltd.",
          raisedDate: "2025-08-25",
          dueDate: "2025-09-24",
          overdueAmount: "₹10,000",
          agingDays: 16,
        },
        {
          key: "2-4",
          raBillNumber: "RAB-2025-009",
          contractor: "NCC Limited",
          raisedDate: "2025-09-02",
          dueDate: "2025-10-01",
          overdueAmount: "₹8,000",
          agingDays: 9,
        },
        {
          key: "2-5",
          raBillNumber: "RAB-2025-010",
          contractor: "Afcons Infrastructure Ltd.",
          raisedDate: "2025-09-03",
          dueDate: "2025-10-02",
          overdueAmount: "₹7,000",
          agingDays: 8,
        },
        {
          key: "2-6",
          raBillNumber: "RAB-2025-011",
          contractor: "Shapoorji Pallonji & Co.",
          raisedDate: "2025-08-30",
          dueDate: "2025-09-29",
          overdueAmount: "₹6,000",
          agingDays: 11,
        },
        {
          key: "2-7",
          raBillNumber: "RAB-2025-012",
          contractor: "GMR Infrastructure",
          raisedDate: "2025-08-26",
          dueDate: "2025-09-25",
          overdueAmount: "₹11,000",
          agingDays: 15,
        },
      ],
      contractors: [
        {
          key: "2-1",
          contractorName: "Simplex Infrastructures",
          woNumber: "WO-2025-006",
          woValue: "₹4,00,000",
          executionPercent: "48%",
          raBillsRaised: "₹1,80,000",
          raBillsPayable: "₹1,20,000",
          outstanding: "₹60,000",
          overdue: "₹18,000",
          agingDays: 10,
        },
        {
          key: "2-2",
          contractorName: "L&T Construction Ltd.",
          woNumber: "WO-2025-007",
          woValue: "₹3,50,000",
          executionPercent: "44%",
          raBillsRaised: "₹1,50,000",
          raBillsPayable: "₹1,00,000",
          outstanding: "₹50,000",
          overdue: "₹15,000",
          agingDays: 12,
        },
        {
          key: "2-3",
          contractorName: "Tata Projects Ltd.",
          woNumber: "WO-2025-008",
          woValue: "₹3,00,000",
          executionPercent: "40%",
          raBillsRaised: "₹1,20,000",
          raBillsPayable: "₹90,000",
          outstanding: "₹30,000",
          overdue: "₹10,000",
          agingDays: 14,
        },
        {
          key: "2-4",
          contractorName: "NCC Limited",
          woNumber: "WO-2025-009",
          woValue: "₹2,80,000",
          executionPercent: "42%",
          raBillsRaised: "₹1,10,000",
          raBillsPayable: "₹80,000",
          outstanding: "₹30,000",
          overdue: "₹8,000",
          agingDays: 11,
        },
        {
          key: "2-5",
          contractorName: "Afcons Infrastructure Ltd.",
          woNumber: "WO-2025-010",
          woValue: "₹2,20,000",
          executionPercent: "38%",
          raBillsRaised: "₹90,000",
          raBillsPayable: "₹70,000",
          outstanding: "₹20,000",
          overdue: "₹7,000",
          agingDays: 13,
        },
        {
          key: "2-6",
          contractorName: "Shapoorji Pallonji & Co.",
          woNumber: "WO-2025-011",
          woValue: "₹1,80,000",
          executionPercent: "45%",
          raBillsRaised: "₹80,000",
          raBillsPayable: "₹60,000",
          outstanding: "₹20,000",
          overdue: "₹6,000",
          agingDays: 10,
        },
        {
          key: "2-7",
          contractorName: "GMR Infrastructure",
          woNumber: "WO-2025-012",
          woValue: "₹1,50,000",
          executionPercent: "50%",
          raBillsRaised: "₹70,000",
          raBillsPayable: "₹50,000",
          outstanding: "₹20,000",
          overdue: "₹11,000",
          agingDays: 15,
        },
      ],
    },
    {
      key: "3",
      projectName: "Elysium Towers",
      woCount: 3,
      woValue: "₹8,00,000",
      executionValue: "₹3,00,000",
      executionPercent: "38%",
      raBillsRaised: "₹3,00,000",
      raBillsApproved: "₹2,50,000",
      raBillsPayable: "₹2,00,000",
      raBillsOutstanding: "₹1,00,000",
      raBillsOverdue: "₹60,000",
      avgAgingDays: 25,
      raBills: [
        {
          key: "3-1",
          raBillNumber: "RAB-2025-013",
          contractor: "Tata Projects Ltd.",
          raisedDate: "2025-08-10",
          dueDate: "2025-09-09",
          overdueAmount: "₹25,000",
          agingDays: 31,
        },
        {
          key: "3-2",
          raBillNumber: "RAB-2025-014",
          contractor: "NCC Limited",
          raisedDate: "2025-08-15",
          dueDate: "2025-09-14",
          overdueAmount: "₹20,000",
          agingDays: 26,
        },
        {
          key: "3-3",
          raBillNumber: "RAB-2025-015",
          contractor: "Afcons Infrastructure Ltd.",
          raisedDate: "2025-08-20",
          dueDate: "2025-09-19",
          overdueAmount: "₹15,000",
          agingDays: 21,
        },
      ],
      contractors: [
        {
          key: "3-1",
          contractorName: "Tata Projects Ltd.",
          woNumber: "WO-2025-013",
          woValue: "₹3,50,000",
          executionPercent: "35%",
          raBillsRaised: "₹1,20,000",
          raBillsPayable: "₹90,000",
          outstanding: "₹30,000",
          overdue: "₹25,000",
          agingDays: 28,
        },
        {
          key: "3-2",
          contractorName: "NCC Limited",
          woNumber: "WO-2025-014",
          woValue: "₹2,80,000",
          executionPercent: "40%",
          raBillsRaised: "₹1,10,000",
          raBillsPayable: "₹70,000",
          outstanding: "₹40,000",
          overdue: "₹20,000",
          agingDays: 24,
        },
        {
          key: "3-3",
          contractorName: "L&T Construction Ltd.",
          woNumber: "WO-2025-015",
          woValue: "₹1,70,000",
          executionPercent: "42%",
          raBillsRaised: "₹70,000",
          raBillsPayable: "₹40,000",
          outstanding: "₹30,000",
          overdue: "₹15,000",
          agingDays: 22,
        },
      ],
    },
    {
      key: "4",
      projectName: "Skyline Royale",
      woCount: 6,
      woValue: "₹14,00,000",
      executionValue: "₹6,00,000",
      executionPercent: "43%",
      raBillsRaised: "₹6,00,000",
      raBillsApproved: "₹5,00,000",
      raBillsPayable: "₹4,00,000",
      raBillsOutstanding: "₹2,00,000",
      raBillsOverdue: "₹80,000",
      avgAgingDays: 15,
      contractors: [
        {
          key: "4-1",
          contractorName: "Shapoorji Pallonji & Co.",
          woNumber: "WO-2025-016",
          woValue: "₹3,20,000",
          executionPercent: "46%",
          raBillsRaised: "₹1,40,000",
          raBillsPayable: "₹1,00,000",
          outstanding: "₹40,000",
          overdue: "₹18,000",
          agingDays: 16,
        },
        {
          key: "4-2",
          contractorName: "Afcons Infrastructure Ltd.",
          woNumber: "WO-2025-017",
          woValue: "₹2,80,000",
          executionPercent: "42%",
          raBillsRaised: "₹1,20,000",
          raBillsPayable: "₹90,000",
          outstanding: "₹30,000",
          overdue: "₹15,000",
          agingDays: 14,
        },
        {
          key: "4-3",
          contractorName: "Simplex Infrastructures",
          woNumber: "WO-2025-018",
          woValue: "₹2,50,000",
          executionPercent: "44%",
          raBillsRaised: "₹1,10,000",
          raBillsPayable: "₹80,000",
          outstanding: "₹30,000",
          overdue: "₹12,000",
          agingDays: 15,
        },
        {
          key: "4-4",
          contractorName: "L&T Construction Ltd.",
          woNumber: "WO-2025-019",
          woValue: "₹2,20,000",
          executionPercent: "40%",
          raBillsRaised: "₹90,000",
          raBillsPayable: "₹70,000",
          outstanding: "₹20,000",
          overdue: "₹10,000",
          agingDays: 13,
        },
        {
          key: "4-5",
          contractorName: "Tata Projects Ltd.",
          woNumber: "WO-2025-020",
          woValue: "₹2,00,000",
          executionPercent: "48%",
          raBillsRaised: "₹95,000",
          raBillsPayable: "₹70,000",
          outstanding: "₹25,000",
          overdue: "₹8,000",
          agingDays: 16,
        },
        {
          key: "4-6",
          contractorName: "NCC Limited",
          woNumber: "WO-2025-021",
          woValue: "₹1,30,000",
          executionPercent: "42%",
          raBillsRaised: "₹55,000",
          raBillsPayable: "₹40,000",
          outstanding: "₹15,000",
          overdue: "₹7,000",
          agingDays: 17,
        },
      ],
    },
  ]);

  // Handle WO Count click → expand row + lazy-load contractors from wo-details
  const handleWOCountClick = (recordKey: string) => {
    if (expandedRowKeys.includes(recordKey)) {
      setExpandedRowKeys(expandedRowKeys.filter((key) => key !== recordKey));
    } else {
      setExpandedRowKeys([...expandedRowKeys, recordKey]);
      const row = woRaTabularData.find((r) => r.key === recordKey);
      if (row && (!row.contractors || row.contractors.length === 0)) {
        fetchWoContractorsForRow(recordKey, row.siteId);
      }
    }
  };

  // Handle RA Bill Avg Aging Days click → ra-bills-aging-summary/details
  const handleRaBillAgingClick = (record: any) => {
    setSelectedRaBillProject(record);
    setRaBillDrawerVisible(true);
    fetchRaBillsAgingDrillDown(record?.siteId);
  };

  // Handle RA Bills Payable click → ra-bills-payable-summary/details
  const handleRaBillPayablesClick = (record: any) => {
    setSelectedRaBillPayablesProject(record);
    setRaBillPayablesDrawerVisible(true);
    fetchRaBillsPayableDrillDown(record?.siteId);
  };

  // Contractor columns for expanded view
  const contractorColumns = [
    {
      title: "Contractor Name",
      dataIndex: "contractorName",
      key: "contractorName",
      width: 200,
      render: (text: string) => (
        <span style={{ fontWeight: 500, color: "#2c3e50" }}>🏢 {text}</span>
      ),
    },
    {
      title: "WO Number",
      dataIndex: "woNumber",
      key: "woNumber",
      width: 130,
      render: (text: string) => (
        <span style={{ fontWeight: 500, color: "#1890ff" }}>{text}</span>
      ),
    },
    {
      title: "WO Value",
      dataIndex: "woValue",
      key: "woValue",
      width: 120,
    },
    {
      title: "Execution %",
      dataIndex: "executionPercent",
      key: "executionPercent",
      width: 110,
      render: (text: string) => {
        const value = parseInt(text);
        const color = value > 45 ? "green" : value > 35 ? "orange" : "red";
        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: "RA Bills Raised",
      dataIndex: "raBillsRaised",
      key: "raBillsRaised",
      width: 130,
      render: (text: string) => (
        <span style={{ color: "#52c41a", fontWeight: 500 }}>{text}</span>
      ),
    },
    {
      title: "RA Bills Payable",
      dataIndex: "raBillsPayable",
      key: "raBillsPayable",
      width: 140,
      render: (text: string) => (
        <span style={{ color: "#722ed1", fontWeight: 500 }}>{text}</span>
      ),
    },
    {
      title: "Outstanding",
      dataIndex: "outstanding",
      key: "outstanding",
      width: 120,
      render: (text: string) => (
        <span style={{ color: "#faad14", fontWeight: 500 }}>{text}</span>
      ),
    },
    {
      title: "Overdue",
      dataIndex: "overdue",
      key: "overdue",
      width: 110,
      render: (text: string) => (
        <span style={{ color: "#ff4d4f", fontWeight: 500 }}>{text}</span>
      ),
    },
    {
      title: "Aging Days",
      dataIndex: "agingDays",
      key: "agingDays",
      width: 110,
      render: (days: number) => {
        const color = days > 20 ? "#ff4d4f" : days > 15 ? "#faad14" : "#52c41a";
        return <span style={{ color, fontWeight: 500 }}>{days} days</span>;
      },
    },
  ];

  // Work Order & RA Bills Table Columns
  const workOrderTableColumns = [
    {
      title: "Project",
      dataIndex: "projectName",
      key: "projectName",
      fixed: "left" as const,
      width: 150,
      render: (text: string) => (
        <span style={{ fontWeight: 600, color: "#1890ff" }}>{text}</span>
      ),
    },
    {
      title: "WO Count",
      dataIndex: "woCount",
      key: "woCount",
      width: 90,
      render: (count: number, record: any) => (
        <a
          onClick={() => handleWOCountClick(record.key)}
          style={{
            cursor: "pointer",
            color: "#1890ff",
            fontWeight: 600,
            textDecoration: "underline",
          }}
        >
          {count} WOs
        </a>
      ),
    },
    {
      title: "WO Value",
      dataIndex: "woValue",
      key: "woValue",
      width: 120,
    },
    {
      title: "Execution",
      key: "execution",
      children: [
        {
          title: "Value (%)",
          dataIndex: "executionValue",
          key: "executionValue",
          width: 140,
          render: (text: string, record: any) => (
            <span>
              {text} ({record.executionPercent})
            </span>
          ),
        },
      ],
    },
    {
      title: "RA Bills Raised",
      dataIndex: "raBillsRaised",
      key: "raBillsRaised",
      width: 130,
      render: (text: string) => (
        <span style={{ color: "#52c41a", fontWeight: 500 }}>{text}</span>
      ),
    },
    {
      title: "RA Bills Approved",
      dataIndex: "raBillsApproved",
      key: "raBillsApproved",
      width: 140,
      render: (text: string) => (
        <span style={{ color: "#1890ff", fontWeight: 500 }}>{text}</span>
      ),
    },
    {
      title: "RA Bills Payable",
      dataIndex: "raBillsPayable",
      key: "raBillsPayable",
      width: 140,
      render: (text: string, record: any) => (
        <a
          onClick={() => handleRaBillPayablesClick(record)}
          style={{ cursor: "pointer" }}
        >
          <span
            style={{
              color: "#722ed1",
              fontWeight: 600,
              textDecoration: "underline",
            }}
          >
            {text}
          </span>
        </a>
      ),
    },
    {
      title: "Outstanding",
      dataIndex: "raBillsOutstanding",
      key: "raBillsOutstanding",
      width: 130,
      render: (text: string) => (
        <span style={{ color: "#faad14", fontWeight: 500 }}>{text}</span>
      ),
    },
    {
      title: "Overdue",
      dataIndex: "raBillsOverdue",
      key: "raBillsOverdue",
      width: 120,
      render: (text: string) => (
        <span style={{ color: "#ff4d4f", fontWeight: 500 }}>{text}</span>
      ),
    },
    {
      title: "Avg Aging Days",
      dataIndex: "avgAgingDays",
      key: "avgAgingDays",
      width: 130,
      render: (days: number, record: any) => {
        const color = days > 20 ? "#ff4d4f" : days > 15 ? "#faad14" : "#52c41a";
        return (
          <a
            onClick={() => handleRaBillAgingClick(record)}
            style={{
              cursor: "pointer",
              color: color,
              fontWeight: 600,
              textDecoration: "underline",
            }}
          >
            {days} days
          </a>
        );
      },
    },
  ];

  // Material Data for Inventory Analytics
  const [materialData] = useState({
    "Grandeur Heights": {
      shortage: [
        {
          key: "1",
          materialName: "Portland Cement",
          plannedQty: 500,
          actualQty: 440,
          unit: "Bags",
          shortage: 60,
          shortagePercent: "12%",
        },
        {
          key: "2",
          materialName: "Steel Rebar (12mm)",
          plannedQty: 200,
          actualQty: 174,
          unit: "Tons",
          shortage: 26,
          shortagePercent: "13%",
        },
        {
          key: "3",
          materialName: "Concrete Blocks",
          plannedQty: 10000,
          actualQty: 8900,
          unit: "Nos",
          shortage: 1100,
          shortagePercent: "11%",
        },
        {
          key: "4",
          materialName: "Sand (M-Sand)",
          plannedQty: 150,
          actualQty: 132,
          unit: "Cubic Meter",
          shortage: 18,
          shortagePercent: "12%",
        },
      ],
      overstock: [
        {
          key: "1",
          materialName: "Paint (Interior)",
          plannedQty: 100,
          actualQty: 105,
          unit: "Liters",
          overstock: 5,
          overstockPercent: "5%",
        },
        {
          key: "2",
          materialName: "Tiles (Ceramic)",
          plannedQty: 2000,
          actualQty: 2100,
          unit: "Sq.Ft",
          overstock: 100,
          overstockPercent: "5%",
        },
      ],
      turnover: [
        {
          key: "1",
          materialName: "Portland Cement",
          deliveryDate: "2025-08-01",
          consumptionDate: "2025-08-18",
          daysInInventory: 17,
        },
        {
          key: "2",
          materialName: "Steel Rebar (12mm)",
          deliveryDate: "2025-07-15",
          consumptionDate: "2025-08-05",
          daysInInventory: 21,
        },
        {
          key: "3",
          materialName: "Concrete Blocks",
          deliveryDate: "2025-08-10",
          consumptionDate: "2025-08-25",
          daysInInventory: 15,
        },
        {
          key: "4",
          materialName: "Sand (M-Sand)",
          deliveryDate: "2025-07-20",
          consumptionDate: "2025-08-12",
          daysInInventory: 23,
        },
      ],
      stockAge: [
        {
          key: "1",
          materialName: "Portland Cement",
          deliveryDate: "2025-07-25",
          currentStock: 440,
          unit: "Bags",
          agingDays: 46,
        },
        {
          key: "2",
          materialName: "Steel Rebar (12mm)",
          deliveryDate: "2025-07-10",
          currentStock: 174,
          unit: "Tons",
          agingDays: 61,
        },
        {
          key: "3",
          materialName: "Concrete Blocks",
          deliveryDate: "2025-08-05",
          currentStock: 8900,
          unit: "Nos",
          agingDays: 35,
        },
        {
          key: "4",
          materialName: "Sand (M-Sand)",
          deliveryDate: "2025-07-20",
          currentStock: 132,
          unit: "Cubic Meter",
          agingDays: 51,
        },
        {
          key: "5",
          materialName: "Paint (Interior)",
          deliveryDate: "2025-08-01",
          currentStock: 105,
          unit: "Liters",
          agingDays: 39,
        },
        {
          key: "6",
          materialName: "Tiles (Ceramic)",
          deliveryDate: "2025-07-28",
          currentStock: 2100,
          unit: "Sq.Ft",
          agingDays: 43,
        },
      ],
    },
    "Paramount Rise": {
      shortage: [
        {
          key: "1",
          materialName: "Ready Mix Concrete",
          plannedQty: 300,
          actualQty: 276,
          unit: "Cubic Meter",
          shortage: 24,
          shortagePercent: "8%",
        },
        {
          key: "2",
          materialName: "Electrical Cables",
          plannedQty: 500,
          actualQty: 460,
          unit: "Meters",
          shortage: 40,
          shortagePercent: "8%",
        },
        {
          key: "3",
          materialName: "Plywood Sheets",
          plannedQty: 100,
          actualQty: 92,
          unit: "Sheets",
          shortage: 8,
          shortagePercent: "8%",
        },
      ],
      overstock: [
        {
          key: "1",
          materialName: "PVC Pipes",
          plannedQty: 200,
          actualQty: 206,
          unit: "Meters",
          overstock: 6,
          overstockPercent: "3%",
        },
      ],
      turnover: [
        {
          key: "1",
          materialName: "Ready Mix Concrete",
          deliveryDate: "2025-08-05",
          consumptionDate: "2025-08-17",
          daysInInventory: 12,
        },
        {
          key: "2",
          materialName: "Electrical Cables",
          deliveryDate: "2025-08-01",
          consumptionDate: "2025-08-15",
          daysInInventory: 14,
        },
        {
          key: "3",
          materialName: "Plywood Sheets",
          deliveryDate: "2025-07-25",
          consumptionDate: "2025-08-08",
          daysInInventory: 14,
        },
      ],
      stockAge: [
        {
          key: "1",
          materialName: "Ready Mix Concrete",
          deliveryDate: "2025-08-15",
          currentStock: 276,
          unit: "Cubic Meter",
          agingDays: 25,
        },
        {
          key: "2",
          materialName: "Electrical Cables",
          deliveryDate: "2025-08-05",
          currentStock: 460,
          unit: "Meters",
          agingDays: 35,
        },
        {
          key: "3",
          materialName: "Plywood Sheets",
          deliveryDate: "2025-08-10",
          currentStock: 92,
          unit: "Sheets",
          agingDays: 30,
        },
        {
          key: "4",
          materialName: "PVC Pipes",
          deliveryDate: "2025-08-01",
          currentStock: 206,
          unit: "Meters",
          agingDays: 39,
        },
      ],
    },
    "Elysium Towers": {
      shortage: [
        {
          key: "1",
          materialName: "AAC Blocks",
          plannedQty: 8000,
          actualQty: 6800,
          unit: "Nos",
          shortage: 1200,
          shortagePercent: "15%",
        },
        {
          key: "2",
          materialName: "Granite Slabs",
          plannedQty: 500,
          actualQty: 425,
          unit: "Sq.Ft",
          shortage: 75,
          shortagePercent: "15%",
        },
        {
          key: "3",
          materialName: "Aluminum Windows",
          plannedQty: 50,
          actualQty: 43,
          unit: "Nos",
          shortage: 7,
          shortagePercent: "14%",
        },
      ],
      overstock: [
        {
          key: "1",
          materialName: "Cement Mortar",
          plannedQty: 200,
          actualQty: 214,
          unit: "Bags",
          overstock: 14,
          overstockPercent: "7%",
        },
        {
          key: "2",
          materialName: "Paint (Exterior)",
          plannedQty: 150,
          actualQty: 160,
          unit: "Liters",
          overstock: 10,
          overstockPercent: "6.7%",
        },
      ],
      turnover: [
        {
          key: "1",
          materialName: "AAC Blocks",
          deliveryDate: "2025-07-10",
          consumptionDate: "2025-08-20",
          daysInInventory: 41,
        },
        {
          key: "2",
          materialName: "Granite Slabs",
          deliveryDate: "2025-07-15",
          consumptionDate: "2025-08-28",
          daysInInventory: 44,
        },
        {
          key: "3",
          materialName: "Aluminum Windows",
          deliveryDate: "2025-07-05",
          consumptionDate: "2025-08-18",
          daysInInventory: 44,
        },
      ],
      stockAge: [
        {
          key: "1",
          materialName: "AAC Blocks",
          deliveryDate: "2025-07-01",
          currentStock: 6800,
          unit: "Nos",
          agingDays: 70,
        },
        {
          key: "2",
          materialName: "Granite Slabs",
          deliveryDate: "2025-07-10",
          currentStock: 425,
          unit: "Sq.Ft",
          agingDays: 61,
        },
        {
          key: "3",
          materialName: "Aluminum Windows",
          deliveryDate: "2025-07-05",
          currentStock: 43,
          unit: "Nos",
          agingDays: 66,
        },
        {
          key: "4",
          materialName: "Cement Mortar",
          deliveryDate: "2025-07-20",
          currentStock: 214,
          unit: "Bags",
          agingDays: 51,
        },
        {
          key: "5",
          materialName: "Paint (Exterior)",
          deliveryDate: "2025-07-25",
          currentStock: 160,
          unit: "Liters",
          agingDays: 46,
        },
      ],
    },
  });

  // Inventory Analytics Data
  const [inventoryData] = useState([
    {
      key: "1",
      projectName: "Grandeur Heights",
      shortage: "12%",
      overstock: "5%",
      avgStockAge: "45 days",
      turnoverRate: "2.5x",
    },
    {
      key: "2",
      projectName: "Paramount Rise",
      shortage: "8%",
      overstock: "3%",
      avgStockAge: "30 days",
      turnoverRate: "3.2x",
    },
    {
      key: "3",
      projectName: "Elysium Towers",
      shortage: "15%",
      overstock: "7%",
      avgStockAge: "60 days",
      turnoverRate: "1.8x",
    },
  ]);

  // Handle inventory column click → fetch drill-down details from API
  const handleInventoryClick = (
    record: any,
    type: "shortage" | "overstock" | "turnover" | "stockAge"
  ) => {
    setInventoryDrawerType(type);
    setInventoryDrawerData({
      projectName: record?.projectName,
      siteId: record?.siteId,
      materials: [],
    });
    setInventoryDrawerVisible(true);
    fetchInventoryDrillDown(type, record?.siteId);
  };

  const inventoryColumns = [
    {
      title: "Project Name",
      dataIndex: "projectName",
      key: "projectName",
      render: (text: string) => (
        <span style={{ fontWeight: 600, color: "#1890ff" }}>{text}</span>
      ),
    },
    {
      title: "Shortage %",
      dataIndex: "shortage",
      key: "shortage",
      render: (text: string, record: any) => (
        <div
          onClick={() => handleInventoryClick(record, "shortage")}
          style={{
            color: "red",
            cursor: "pointer",
            textDecoration: "underline",
          }}
        >
          {text}
        </div>
      ),
    },
    {
      title: "Overstock %",
      dataIndex: "overstock",
      key: "overstock",
      render: (text: string, record: any) => (
        <div
          onClick={() => handleInventoryClick(record, "overstock")}
          style={{
            color: "orange",
            cursor: "pointer",
            textDecoration: "underline",
          }}
          color="orange"
        >
          {text}
        </div>
      ),
    },
    {
      title: "Avg Stock Age",
      dataIndex: "avgStockAge",
      key: "avgStockAge",
      render: (text: string, record: any) => (
        <a
          onClick={() => handleInventoryClick(record, "stockAge")}
          style={{
            cursor: "pointer",
            color: "#1890ff",
            textDecoration: "underline",
          }}
        >
          {text}
        </a>
      ),
    },
    {
      title: "Turnover Rate",
      dataIndex: "turnoverRate",
      key: "turnoverRate",
      render: (text: string, record: any) => (
        <a
          onClick={() => handleInventoryClick(record, "turnover")}
          style={{
            cursor: "pointer",
            color: "#1890ff",
            textDecoration: "underline",
          }}
        >
          {text}
        </a>
      ),
    },
  ];

  // Budget Variance Drivers Data for each project
  const [budgetVarianceData] = useState({
    "Grandeur Heights": [
      {
        key: "1",
        category: "Civil Works",
        planned: 15000000,
        actual: 14200000,
        variance: -800000,
        variancePercent: "-5.33%",
      },
      {
        key: "2",
        category: "MEP Installation",
        planned: 10000000,
        actual: 11500000,
        variance: 1500000,
        variancePercent: "+15%",
      },
      {
        key: "3",
        category: "Electrical Systems",
        planned: 8000000,
        actual: 8200000,
        variance: 200000,
        variancePercent: "+2.5%",
      },
      {
        key: "4",
        category: "HVAC Systems",
        planned: 7000000,
        actual: 6800000,
        variance: -200000,
        variancePercent: "-2.86%",
      },
      {
        key: "5",
        category: "Plumbing Works",
        planned: 5000000,
        actual: 5100000,
        variance: 100000,
        variancePercent: "+2%",
      },
      {
        key: "6",
        category: "Finishing Works",
        planned: 3000000,
        actual: 3400000,
        variance: 400000,
        variancePercent: "+13.33%",
      },
      {
        key: "7",
        category: "Safety & Compliance",
        planned: 2000000,
        actual: 1800000,
        variance: -200000,
        variancePercent: "-10%",
      },
    ],
    "Paramount Rise": [
      {
        key: "1",
        category: "Foundation & Structural",
        planned: 20000000,
        actual: 19500000,
        variance: -500000,
        variancePercent: "-2.5%",
      },
      {
        key: "2",
        category: "Civil Works",
        planned: 18000000,
        actual: 19200000,
        variance: 1200000,
        variancePercent: "+6.67%",
      },
      {
        key: "3",
        category: "MEP Installation",
        planned: 12000000,
        actual: 12800000,
        variance: 800000,
        variancePercent: "+6.67%",
      },
      {
        key: "4",
        category: "Interior Design",
        planned: 10000000,
        actual: 10500000,
        variance: 500000,
        variancePercent: "+5%",
      },
      {
        key: "5",
        category: "Electrical Systems",
        planned: 8000000,
        actual: 7900000,
        variance: -100000,
        variancePercent: "-1.25%",
      },
      {
        key: "6",
        category: "Landscaping",
        planned: 5000000,
        actual: 5400000,
        variance: 400000,
        variancePercent: "+8%",
      },
      {
        key: "7",
        category: "Security Systems",
        planned: 2000000,
        actual: 1900000,
        variance: -100000,
        variancePercent: "-5%",
      },
    ],
    "Elysium Towers": [
      {
        key: "1",
        category: "Design & Planning",
        planned: 8000000,
        actual: 7200000,
        variance: -800000,
        variancePercent: "-10%",
      },
      {
        key: "2",
        category: "Civil Works",
        planned: 10000000,
        actual: 10800000,
        variance: 800000,
        variancePercent: "+8%",
      },
      {
        key: "3",
        category: "Structural Works",
        planned: 9000000,
        actual: 9500000,
        variance: 500000,
        variancePercent: "+5.56%",
      },
      {
        key: "4",
        category: "MEP Installation",
        planned: 6000000,
        actual: 6400000,
        variance: 400000,
        variancePercent: "+6.67%",
      },
      {
        key: "5",
        category: "Finishing Works",
        planned: 4000000,
        actual: 4200000,
        variance: 200000,
        variancePercent: "+5%",
      },
      {
        key: "6",
        category: "External Works",
        planned: 2000000,
        actual: 1800000,
        variance: -200000,
        variancePercent: "-10%",
      },
      {
        key: "7",
        category: "Contingency",
        planned: 1000000,
        actual: 900000,
        variance: -100000,
        variancePercent: "-10%",
      },
    ],
  });

  // Budget MOM Data for each project
  const [budgetMOMData] = useState({
    "Grandeur Heights": [
      {
        key: "1",
        month: "Jan 2025",
        plannedBudget: 5000000,
        actualBudget: 4500000,
        variance: -500000,
        variancePercent: "-10%",
      },
      {
        key: "2",
        month: "Feb 2025",
        plannedBudget: 5000000,
        actualBudget: 5200000,
        variance: 200000,
        variancePercent: "+4%",
      },
      {
        key: "3",
        month: "Mar 2025",
        plannedBudget: 6000000,
        actualBudget: 5500000,
        variance: -500000,
        variancePercent: "-8.3%",
      },
      {
        key: "4",
        month: "Apr 2025",
        plannedBudget: 6000000,
        actualBudget: 6100000,
        variance: 100000,
        variancePercent: "+1.7%",
      },
      {
        key: "5",
        month: "May 2025",
        plannedBudget: 7000000,
        actualBudget: 6800000,
        variance: -200000,
        variancePercent: "-2.9%",
      },
      {
        key: "6",
        month: "Jun 2025",
        plannedBudget: 7000000,
        actualBudget: 7200000,
        variance: 200000,
        variancePercent: "+2.9%",
      },
      {
        key: "7",
        month: "Jul 2025",
        plannedBudget: 8000000,
        actualBudget: 7800000,
        variance: -200000,
        variancePercent: "-2.5%",
      },
      {
        key: "8",
        month: "Aug 2025",
        plannedBudget: 8000000,
        actualBudget: 8100000,
        variance: 100000,
        variancePercent: "+1.25%",
      },
    ],
    "Paramount Rise": [
      {
        key: "1",
        month: "Jan 2025",
        plannedBudget: 8000000,
        actualBudget: 7800000,
        variance: -200000,
        variancePercent: "-2.5%",
      },
      {
        key: "2",
        month: "Feb 2025",
        plannedBudget: 9000000,
        actualBudget: 9200000,
        variance: 200000,
        variancePercent: "+2.2%",
      },
      {
        key: "3",
        month: "Mar 2025",
        plannedBudget: 9000000,
        actualBudget: 8900000,
        variance: -100000,
        variancePercent: "-1.1%",
      },
      {
        key: "4",
        month: "Apr 2025",
        plannedBudget: 10000000,
        actualBudget: 10300000,
        variance: 300000,
        variancePercent: "+3%",
      },
      {
        key: "5",
        month: "May 2025",
        plannedBudget: 10000000,
        actualBudget: 9800000,
        variance: -200000,
        variancePercent: "-2%",
      },
      {
        key: "6",
        month: "Jun 2025",
        plannedBudget: 11000000,
        actualBudget: 11500000,
        variance: 500000,
        variancePercent: "+4.5%",
      },
      {
        key: "7",
        month: "Jul 2025",
        plannedBudget: 11000000,
        actualBudget: 10900000,
        variance: -100000,
        variancePercent: "-0.9%",
      },
      {
        key: "8",
        month: "Aug 2025",
        plannedBudget: 12000000,
        actualBudget: 12200000,
        variance: 200000,
        variancePercent: "+1.7%",
      },
    ],
    "Elysium Towers": [
      {
        key: "1",
        month: "Jan 2025",
        plannedBudget: 4000000,
        actualBudget: 3800000,
        variance: -200000,
        variancePercent: "-5%",
      },
      {
        key: "2",
        month: "Feb 2025",
        plannedBudget: 4500000,
        actualBudget: 4200000,
        variance: -300000,
        variancePercent: "-6.7%",
      },
      {
        key: "3",
        month: "Mar 2025",
        plannedBudget: 4500000,
        actualBudget: 4600000,
        variance: 100000,
        variancePercent: "+2.2%",
      },
      {
        key: "4",
        month: "Apr 2025",
        plannedBudget: 5000000,
        actualBudget: 4800000,
        variance: -200000,
        variancePercent: "-4%",
      },
      {
        key: "5",
        month: "May 2025",
        plannedBudget: 5000000,
        actualBudget: 5100000,
        variance: 100000,
        variancePercent: "+2%",
      },
      {
        key: "6",
        month: "Jun 2025",
        plannedBudget: 5500000,
        actualBudget: 5300000,
        variance: -200000,
        variancePercent: "-3.6%",
      },
      {
        key: "7",
        month: "Jul 2025",
        plannedBudget: 6000000,
        actualBudget: 5800000,
        variance: -200000,
        variancePercent: "-3.3%",
      },
      {
        key: "8",
        month: "Aug 2025",
        plannedBudget: 6000000,
        actualBudget: 6100000,
        variance: 100000,
        variancePercent: "+1.7%",
      },
    ],
  });

  // Budget Analytics Data
  const [budgetData] = useState([
    {
      key: "1",
      project: "Grandeur Heights",
      budget: "₹50,00,000",
      committed: "₹45,00,000",
      actual: "₹22,50,000",
      utilization: "45%",
      variance: "₹22,50,000",
    },
    {
      key: "2",
      project: "Paramount Rise",
      budget: "₹75,00,000",
      committed: "₹70,00,000",
      actual: "₹54,00,000",
      utilization: "72%",
      variance: "₹16,00,000",
    },
    {
      key: "3",
      project: "Elysium Towers",
      budget: "₹40,00,000",
      committed: "₹35,00,000",
      actual: "₹11,20,000",
      utilization: "28%",
      variance: "₹23,80,000",
    },
  ]);

  // Handle budget project click → budget-variance drill-down
  const handleBudgetProjectClick = (record: any) => {
    const momData = budgetMOMData[record.project as keyof typeof budgetMOMData];
    const projectData = { ...record, momData: momData || [] };
    setSelectedBudgetProject(projectData);
    // Reset stale rows from a previous click before re-fetching.
    setBudgetMomRows([]);
    setBudgetVarianceList([]);
    setBudgetVarianceSummary(null);
    setBudgetDrawerVisible(true);
    fetchBudgetVarianceDrillDown(record?.siteId);
  };

  // Handle variance value click → budget-variance-details
  const handleVarianceClick = (record: any) => {
    const varianceData =
      budgetVarianceData[record.project as keyof typeof budgetVarianceData];
    const projectData = { ...record, varianceData: varianceData || [] };
    setSelectedVarianceProject(projectData);
    setBudgetMomRows([]);
    setBudgetVarianceList([]);
    setBudgetVarianceSummary(null);
    setVarianceDrawerVisible(true);
    fetchBudgetVarianceDrillDown(record?.siteId);
  };

  const budgetColumns = [
    {
      title: "Project",
      dataIndex: "project",
      key: "project",
      render: (text: string) => (
        <span style={{ fontWeight: 600, color: "#1890ff" }}>{text}</span>
      ),
    },
    {
      title: "Budget",
      dataIndex: "budget",
      key: "budget",
    },
    {
      title: "Committed",
      dataIndex: "committed",
      key: "committed",
    },
    {
      title: "Actual",
      dataIndex: "actual",
      key: "actual",
    },
    {
      title: "Utilization",
      dataIndex: "utilization",
      key: "utilization",
      render: (text: string) => {
        const value = parseInt(text);
        const color = value > 80 ? "red" : value > 50 ? "orange" : "green";
        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: "Variance",
      dataIndex: "variance",
      key: "variance",
      render: (text: string, record: any) => (
        <a
          onClick={() => handleVarianceClick(record)}
          style={{
            cursor: "pointer",
            color: "#1890ff",
            textDecoration: "underline",
            fontWeight: 500,
          }}
        >
          {text}
        </a>
      ),
    },
  ];

  // Contractor Summary Data
  const [contractorData] = useState([
    {
      key: "1",
      contractor: "L&T Construction Ltd.",
      woValue: 1500000,
      percentExecuted: 65,
      raBillsAmount: 500000,
      paymentsAmount: 300000,
      outstanding: 200000,
      overdue: 50000,
      avgAgingDays: 25,
    },
    {
      key: "2",
      contractor: "Shapoorji Pallonji & Co.",
      woValue: 1200000,
      percentExecuted: 48,
      raBillsAmount: 350000,
      paymentsAmount: 200000,
      outstanding: 150000,
      overdue: 30000,
      avgAgingDays: 18,
    },
    {
      key: "3",
      contractor: "Tata Projects Ltd.",
      woValue: 1800000,
      percentExecuted: 72,
      raBillsAmount: 600000,
      paymentsAmount: 300000,
      outstanding: 300000,
      overdue: 80000,
      avgAgingDays: 32,
    },
    {
      key: "4",
      contractor: "Afcons Infrastructure Ltd.",
      woValue: 2500000,
      percentExecuted: 85,
      raBillsAmount: 900000,
      paymentsAmount: 700000,
      outstanding: 200000,
      overdue: 40000,
      avgAgingDays: 12,
    },
    {
      key: "5",
      contractor: "NCC Limited",
      woValue: 950000,
      percentExecuted: 55,
      raBillsAmount: 400000,
      paymentsAmount: 250000,
      outstanding: 150000,
      overdue: 60000,
      avgAgingDays: 22,
    },
    {
      key: "6",
      contractor: "Simplex Infrastructures",
      woValue: 2100000,
      percentExecuted: 78,
      raBillsAmount: 750000,
      paymentsAmount: 550000,
      outstanding: 200000,
      overdue: 35000,
      avgAgingDays: 16,
    },
  ]);

  // Helper functions for Contractor Summary
  const formatCurrency = (amount: number) => {
    if (amount >= 10000000) {
      return `₹${(amount / 10000000).toFixed(1)}Cr`;
    } else if (amount >= 100000) {
      return `₹${(amount / 100000).toFixed(1)}L`;
    } else if (amount >= 1000) {
      return `₹${(amount / 1000).toFixed(1)}K`;
    }
    return `₹${amount}`;
  };

  const getStatusColor = (contractor: any, index: number) => {
    const colors = ["#3498db", "#e74c3c", "#27ae60", "#f39c12", "#9b59b6"];
    return colors[index % colors.length];
  };

  const getPerformanceRating = (
    percentExecuted: number,
    avgAgingDays: number
  ) => {
    if (percentExecuted >= 80 && avgAgingDays <= 15)
      return { rating: "Excellent", color: "#27ae60" };
    if (percentExecuted >= 70 && avgAgingDays <= 20)
      return { rating: "Good", color: "#f39c12" };
    if (percentExecuted >= 60) return { rating: "Average", color: "#e67e22" };
    return { rating: "Poor", color: "#e74c3c" };
  };

  // Custom arrow components
  const CustomPrevArrow = (props: any) => {
    const { onClick } = props;
    return (
      <div
        onClick={onClick}
        style={{
          position: "absolute",
          left: "-10px",
          top: "50%",
          transform: "translateY(-50%)",
          zIndex: 10,
          backgroundColor: "white",
          borderRadius: "50%",
          width: "30px",
          height: "30px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          transition: "all 0.3s ease",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = "white";
          e.currentTarget.style.transform = "translateY(-50%) scale(1.1)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = "white";
          e.currentTarget.style.transform = "translateY(-50%) scale(1)";
        }}
      >
        <span
          style={{ color: "#1890ff", fontSize: "14px", fontWeight: "bold" }}
        >
          ❮
        </span>
      </div>
    );
  };

  const CustomNextArrow = (props: any) => {
    const { onClick } = props;
    return (
      <div
        onClick={onClick}
        style={{
          position: "absolute",
          right: "-10px",
          top: "50%",
          transform: "translateY(-50%)",
          zIndex: 10,
          backgroundColor: "white",
          borderRadius: "50%",
          width: "30px",
          height: "30px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          transition: "all 0.3s ease",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = "white";
          e.currentTarget.style.transform = "translateY(-50%) scale(1.1)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = "white";
          e.currentTarget.style.transform = "translateY(-50%) scale(1)";
        }}
      >
        <span
          style={{ color: "#1890ff", fontSize: "14px", fontWeight: "bold" }}
        >
          ❯
        </span>
      </div>
    );
  };

  // Custom carousel settings
  const carouselSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 4000,
    arrows: true,
    prevArrow: <CustomPrevArrow />,
    nextArrow: <CustomNextArrow />,
    responsive: [
      {
        breakpoint: 1200,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
          arrows: true,
          prevArrow: <CustomPrevArrow />,
          nextArrow: <CustomNextArrow />,
        },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          arrows: true,
          prevArrow: <CustomPrevArrow />,
          nextArrow: <CustomNextArrow />,
        },
      },
    ],
  };

  return (
    <React.Fragment>
      <Head title="Main Dashboard" />
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle page>Consolidated Transactional Analytics Dashboard(CTAD)</BlockTitle>
              <BlockDes className="text-soft">
                <p>Comprehensive overview of all projects</p>
              </BlockDes>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        {/* Executive Summary Section */}
        <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
          <Col xs={24}>
            <Card
              title={
                <div>
                  <div
                    style={{
                      fontSize: "16px",
                      fontWeight: 600,
                      marginBottom: "4px",
                    }}
                  >
                    Executive Summary
                  </div>
                  <div
                    style={{
                      fontSize: "13px",
                      fontWeight: 400,
                      color: "#8c8c8c",
                    }}
                  >
                    Overview of key project metrics and performance indicators
                  </div>
                </div>
              }
              bordered={false}
            >
              <Table
                columns={executiveSummaryColumns}
                dataSource={executiveSummaryData}
                loading={executiveSummaryLoading}
                pagination={executiveSummaryPagination}
                onChange={handleExecutiveSummaryTableChange}
                scroll={{ x: 1200 }}
                size="small"
                bordered
              />
            </Card>
          </Col>
        </Row>

        {/* Project Management Analytics Section */}
        <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
          <Col xs={24}>
            <Card
              title={
                <div>
                  <div
                    style={{
                      fontSize: "16px",
                      fontWeight: 600,
                      marginBottom: "4px",
                    }}
                  >
                    Project Management Analytics
                  </div>
                  <div
                    style={{
                      fontSize: "13px",
                      fontWeight: 400,
                      color: "#8c8c8c",
                    }}
                  >
                    Track project progress, phases, tasks, and overall
                    completion status
                  </div>
                </div>
              }
              bordered={false}
              extra={
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <BarChartOutlined
                    style={{
                      fontSize: 16,
                      color:
                        projectMgmtViewMode === "graph" ? "#1890ff" : "#999",
                    }}
                  />
                  <Switch
                    checked={projectMgmtViewMode === "table"}
                    onChange={(checked) =>
                      setProjectMgmtViewMode(checked ? "table" : "graph")
                    }
                    size="small"
                  />
                  <TableOutlined
                    style={{
                      fontSize: 16,
                      color:
                        projectMgmtViewMode === "table" ? "#1890ff" : "#999",
                    }}
                  />
                </div>
              }
            >
              {projectMgmtViewMode === "graph" ? (
                <div style={{ overflowX: "auto", overflowY: "hidden" }}>
                  <div
                    style={{
                      minWidth: `${Math.max(
                        1600,
                        projectAnalyticsGraphData.length * 200
                      )}px`,
                      height: 400,
                    }}
                  >
                    <ReactECharts
                      option={projectManagementChartOptions}
                      style={{ height: 400, width: "100%" }}
                    />
                  </div>
                </div>
              ) : (
                <Table
                  columns={projectMgmtTableColumns}
                  dataSource={projectAnalyticsTabularData}
                  loading={projectAnalyticsLoading}
                  pagination={projectAnalyticsPagination}
                  onChange={handleProjectAnalyticsTableChange}
                  size="small"
                  bordered
                  expandable={{
                    childrenColumnName: "noExpand",
                  }}
                />
              )}
            </Card>
          </Col>
        </Row>

        {/* Procurement and Payables Section */}
        <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
          <Col xs={24}>
            <Card
              title={
                <div>
                  <div
                    style={{
                      fontSize: "16px",
                      fontWeight: 600,
                      marginBottom: "4px",
                    }}
                  >
                    Procurement and Payables
                  </div>
                  <div
                    style={{
                      fontSize: "13px",
                      fontWeight: 400,
                      color: "#8c8c8c",
                    }}
                  >
                    Monitor purchase orders, vendor invoices, and payment status
                  </div>
                </div>
              }
              bordered={false}
              extra={
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <BarChartOutlined
                    style={{
                      fontSize: 16,
                      color:
                        procurementViewMode === "graph" ? "#1890ff" : "#999",
                    }}
                  />
                  <Switch
                    checked={procurementViewMode === "table"}
                    onChange={(checked) =>
                      setProcurementViewMode(checked ? "table" : "graph")
                    }
                    size="small"
                  />
                  <TableOutlined
                    style={{
                      fontSize: 16,
                      color:
                        procurementViewMode === "table" ? "#1890ff" : "#999",
                    }}
                  />
                </div>
              }
            >
              {procurementViewMode === "graph" ? (
                <div style={{ overflowX: "auto", overflowY: "hidden" }}>
                  <div
                    style={{
                      minWidth: `${Math.max(
                        1600,
                        procurementGraphData.length * 200
                      )}px`,
                      height: 400,
                    }}
                  >
                    <ReactECharts
                      option={procurementPayablesChartOptions}
                      style={{ height: 400, width: "100%" }}
                    />
                  </div>
                </div>
              ) : (
                <Table
                  columns={procurementTableColumns}
                  dataSource={procurementTabularData}
                  loading={procurementLoading}
                  pagination={procurementPagination}
                  onChange={handleProcurementTableChange}
                  size="small"
                  bordered
                  scroll={{ x: 800 }}
                />
              )}
            </Card>
          </Col>
        </Row>

        {/* Inventory Analytics Section */}
        <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
          <Col xs={24}>
            <Card
              title={
                <div>
                  <div
                    style={{
                      fontSize: "16px",
                      fontWeight: 600,
                      marginBottom: "4px",
                    }}
                  >
                    Inventory Analytics
                  </div>
                  <div
                    style={{
                      fontSize: "13px",
                      fontWeight: 400,
                      color: "#8c8c8c",
                    }}
                  >
                    Material procurement and consumption tracking across
                    projects
                  </div>
                </div>
              }
              bordered={false}
            >
              <Table
                columns={inventoryColumns}
                dataSource={inventorySummaryData}
                loading={inventoryLoading}
                pagination={inventoryPagination}
                onChange={handleInventoryTableChange}
                size="small"
                bordered
              />
            </Card>
          </Col>
        </Row>

        {/* Work Order & RA Bills Section */}
        <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
          <Col xs={24}>
            <Card
              title={
                <div>
                  <div
                    style={{
                      fontSize: "16px",
                      fontWeight: 600,
                      marginBottom: "4px",
                    }}
                  >
                    Work Order & RA Bills
                  </div>
                  <div
                    style={{
                      fontSize: "13px",
                      fontWeight: 400,
                      color: "#8c8c8c",
                    }}
                  >
                    Contractor work orders and RA bills payables analysis
                  </div>
                </div>
              }
              bordered={false}
              extra={
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <BarChartOutlined
                    style={{
                      fontSize: 16,
                      color: workOrderViewMode === "graph" ? "#1890ff" : "#999",
                    }}
                  />
                  <Switch
                    checked={workOrderViewMode === "table"}
                    onChange={(checked) =>
                      setWorkOrderViewMode(checked ? "table" : "graph")
                    }
                    size="small"
                  />
                  <TableOutlined
                    style={{
                      fontSize: 16,
                      color: workOrderViewMode === "table" ? "#1890ff" : "#999",
                    }}
                  />
                </div>
              }
            >
              {workOrderViewMode === "graph" ? (
                <div style={{ overflowX: "auto", overflowY: "hidden" }}>
                  <div
                    style={{
                      minWidth: `${Math.max(
                        1600,
                        woRaGraphData.length * 200
                      )}px`,
                      height: 400,
                    }}
                  >
                    <ReactECharts
                      option={workOrderChartOptions}
                      style={{ height: 400, width: "100%" }}
                    />
                  </div>
                </div>
              ) : (
                <Table
                  columns={workOrderTableColumns}
                  dataSource={woRaTabularData}
                  loading={woRaLoading}
                  pagination={woRaPagination}
                  onChange={handleWoRaTableChange}
                  size="small"
                  bordered
                  scroll={{ x: 1200 }}
                  expandable={{
                    expandedRowKeys: expandedRowKeys,
                    onExpand: (expanded, record) => {
                      handleWOCountClick(record.key);
                    },
                    expandIcon: () => null,
                    columnWidth: 0,
                    expandedRowRender: (record: any) => (
                      <div
                        style={{
                          margin: 0,
                          padding: "16px 0",
                          backgroundColor: "#fafafa",
                        }}
                      >
                        <div
                          style={{
                            marginBottom: 16,
                            padding: "12px 16px",
                            backgroundColor: "#e6f7ff",
                            borderRadius: "4px",
                            borderLeft: "4px solid #1890ff",
                          }}
                        >
                          <span
                            style={{
                              fontWeight: 600,
                              color: "#1890ff",
                              fontSize: "16px",
                            }}
                          >
                            🏗️ Contractor Summary - {record.projectName}
                          </span>
                        </div>
                        <div style={{ padding: "0", position: "relative" }}>
                          <Carousel {...carouselSettings}>
                            {record.contractors.map(
                              (contractor: any, index: number) => {
                                const statusColor = getStatusColor(
                                  contractor,
                                  index
                                );
                                return (
                                  <div
                                    key={index}
                                    style={{ padding: "0", margin: "0" }}
                                  >
                                    <Card
                                      title={
                                        <div
                                          style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                          }}
                                        >
                                          <span
                                            style={{
                                              color: "#2c3e50",
                                              fontSize: "15px",
                                            }}
                                          >
                                            🏢 {contractor.contractorName}
                                          </span>
                                          <span
                                            style={{
                                              color: "#1890ff",
                                              fontSize: "12px",
                                              fontWeight: 500,
                                            }}
                                          >
                                            {contractor.woNumber}
                                          </span>
                                        </div>
                                      }
                                      size="small"
                                      style={{
                                        borderLeft: `4px solid ${statusColor}`,
                                        height: "320px",
                                        boxShadow: "0 3px 8px rgba(0,0,0,0.12)",
                                        borderRadius: "8px",
                                        width: "100%",
                                        maxWidth: "460px",
                                      }}
                                      headStyle={{
                                        backgroundColor: "#f8f9fa",
                                        borderRadius: "8px 8px 0 0",
                                      }}
                                    >
                                      <div
                                        style={{
                                          fontSize: "13px",
                                          lineHeight: "1.4",
                                        }}
                                      >
                                        {/* WO Value */}
                                        <div
                                          style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            marginBottom: "8px",
                                            padding: "6px",
                                            backgroundColor: "#f8f9fa",
                                            borderRadius: "4px",
                                          }}
                                        >
                                          <span>
                                            <strong>WO Value:</strong>
                                          </span>
                                          <span
                                            style={{
                                              fontWeight: "bold",
                                              color: "#2c3e50",
                                              fontSize: "13px",
                                            }}
                                          >
                                            {contractor.woValue}
                                          </span>
                                        </div>

                                        {/* Execution Progress */}
                                        <div style={{ marginBottom: "10px" }}>
                                          <div
                                            style={{
                                              display: "flex",
                                              justifyContent: "space-between",
                                              marginBottom: "4px",
                                            }}
                                          >
                                            <span>
                                              <strong>% Executed:</strong>
                                            </span>
                                            <span
                                              style={{
                                                fontWeight: "bold",
                                                color: statusColor,
                                                fontSize: "13px",
                                              }}
                                            >
                                              {contractor.executionPercent}
                                            </span>
                                          </div>
                                          <Progress
                                            percent={parseInt(
                                              contractor.executionPercent
                                            )}
                                            size="small"
                                            strokeColor={statusColor}
                                            style={{ marginTop: "4px" }}
                                            showInfo={false}
                                          />
                                        </div>

                                        {/* RA Bills and Payments */}
                                        <Row
                                          gutter={8}
                                          style={{ marginBottom: "8px" }}
                                        >
                                          <Col span={12}>
                                            <div
                                              style={{
                                                backgroundColor: "#e8f5e8",
                                                padding: "6px",
                                                borderRadius: "4px",
                                                textAlign: "center",
                                              }}
                                            >
                                              <div
                                                style={{
                                                  fontSize: "13px",
                                                  color: "#666",
                                                  marginBottom: "1px",
                                                }}
                                              >
                                                RA Bills Raised
                                              </div>
                                              <div
                                                style={{
                                                  fontWeight: "bold",
                                                  fontSize: "13px",
                                                  color: "#27ae60",
                                                }}
                                              >
                                                {contractor.raBillsRaised}
                                              </div>
                                            </div>
                                          </Col>
                                          <Col span={12}>
                                            <div
                                              style={{
                                                backgroundColor: "#e3f2fd",
                                                padding: "6px",
                                                borderRadius: "4px",
                                                textAlign: "center",
                                              }}
                                            >
                                              <div
                                                style={{
                                                  fontSize: "13px",
                                                  color: "#666",
                                                  marginBottom: "1px",
                                                }}
                                              >
                                                RA Bills Payable
                                              </div>
                                              <div
                                                style={{
                                                  fontWeight: "bold",
                                                  fontSize: "13px",
                                                  color: "#3498db",
                                                }}
                                              >
                                                {contractor.raBillsPayable}
                                              </div>
                                            </div>
                                          </Col>
                                        </Row>

                                        {/* Financial Details */}
                                        <div
                                          style={{
                                            borderTop: "1px solid #eee",
                                            paddingTop: "6px",
                                          }}
                                        >
                                          <div
                                            style={{
                                              display: "flex",
                                              justifyContent: "space-between",
                                              marginBottom: "4px",
                                            }}
                                          >
                                            <span>
                                              <strong>Outstanding:</strong>
                                            </span>
                                            <span
                                              style={{
                                                fontWeight: "bold",
                                                color: "#f39c12",
                                                fontSize: "13px",
                                              }}
                                            >
                                              {contractor.outstanding}
                                            </span>
                                          </div>

                                          <div
                                            style={{
                                              display: "flex",
                                              justifyContent: "space-between",
                                              marginBottom: "4px",
                                            }}
                                          >
                                            <span>
                                              <strong>Overdue:</strong>
                                            </span>
                                            <span
                                              style={{
                                                fontWeight: "bold",
                                                color: "#e74c3c",
                                                fontSize: "13px",
                                              }}
                                            >
                                              {contractor.overdue}
                                            </span>
                                          </div>

                                          <div
                                            style={{
                                              display: "flex",
                                              justifyContent: "space-between",
                                              alignItems: "center",
                                              marginTop: "6px",
                                              paddingTop: "4px",
                                              borderTop: "1px solid #eee",
                                            }}
                                          >
                                            <span>
                                              <strong>Avg Aging:</strong>
                                            </span>
                                            <span
                                              style={{
                                                backgroundColor:
                                                  contractor.agingDays > 20
                                                    ? "#e74c3c"
                                                    : contractor.agingDays > 15
                                                    ? "#f39c12"
                                                    : "#27ae60",
                                                color: "white",
                                                padding: "3px 8px",
                                                borderRadius: "10px",
                                                fontSize: "13px",
                                                fontWeight: "bold",
                                              }}
                                            >
                                              {contractor.agingDays} days
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                    </Card>
                                  </div>
                                );
                              }
                            )}
                          </Carousel>
                        </div>
                      </div>
                    ),
                    rowExpandable: (record: any) =>
                      record.contractors && record.contractors.length > 0,
                  }}
                />
              )}
            </Card>
          </Col>
        </Row>

        {/* Budget Analytics Section */}
        <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
          <Col xs={24}>
            <Card
              title={
                <div>
                  <div
                    style={{
                      fontSize: "16px",
                      fontWeight: 600,
                      marginBottom: "4px",
                    }}
                  >
                    Budget Analytics
                  </div>
                  <div
                    style={{
                      fontSize: "13px",
                      fontWeight: 400,
                      color: "#8c8c8c",
                    }}
                  >
                    Budget allocation, utilization, and variance analysis
                  </div>
                </div>
              }
              bordered={false}
            >
              <Table
                columns={budgetColumns}
                dataSource={budgetSummaryData}
                loading={budgetLoading}
                pagination={budgetPagination}
                onChange={handleBudgetTableChange}
                size="small"
                bordered
              />
            </Card>
          </Col>
        </Row>

        {/* Project Details Drawer */}
        <Drawer
          title={
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: "18px" }}>📁</span>
              <span>
                {selectedProject?.projectName} - Phases, Activities & Tasks
              </span>
            </div>
          }
          placement="right"
          width={800}
          onClose={() => setDrawerVisible(false)}
          open={drawerVisible}
        >
          {selectedProject && (
            <div>
              <Card size="small" style={{ marginBottom: 16 }}>
                <Row gutter={16}>
                  <Col span={8}>
                    <Statistic
                      title="Actual Progress"
                      value={
                        selectedProjectKpi?.actualProgress !== undefined
                          ? `${selectedProjectKpi.actualProgress}%`
                          : selectedProject.actualProgress
                      }
                      valueStyle={{ color: "#1890ff" }}
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="Avg Delay Days"
                      value={
                        selectedProjectKpi?.avgDelayDays ??
                        selectedProject.avgDelayDays
                      }
                      suffix="days"
                      valueStyle={{
                        color:
                          (selectedProjectKpi?.avgDelayDays ??
                            selectedProject.avgDelayDays) > 10
                            ? "#ff4d4f"
                            : (selectedProjectKpi?.avgDelayDays ??
                                selectedProject.avgDelayDays) > 5
                            ? "#faad14"
                            : "#52c41a",
                      }}
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="Status"
                      value={
                        selectedProjectKpi?.status ??
                        selectedProject.overallStatus
                      }
                      valueStyle={{
                        fontSize: 14,
                        color:
                          (selectedProjectKpi?.status ??
                            selectedProject.overallStatus) === "On Track"
                            ? "#52c41a"
                            : (selectedProjectKpi?.status ??
                                selectedProject.overallStatus) === "Delayed"
                            ? "#faad14"
                            : "#ff4d4f",
                      }}
                    />
                  </Col>
                </Row>
              </Card>

              <Table
                columns={drawerTableColumns}
                dataSource={selectedProjectPhases}
                loading={projectDrillLoading}
                pagination={false}
                size="small"
                bordered
                expandable={{
                  defaultExpandAllRows: false,
                  indentSize: 30,
                  onExpand: (expanded, record: any) => {
                    if (!expanded) return;
                    if (record.type === "phase") {
                      togglePhaseExpand(record);
                    } else if (record.type === "activity") {
                      const parentPhase = selectedProjectPhases.find(
                        (p: any) =>
                          (p.children || []).some(
                            (c: any) => c.key === record.key
                          )
                      );
                      if (parentPhase) {
                        toggleActivityExpand(parentPhase.key, record);
                      }
                    }
                  },
                }}
              />
            </div>
          )}
        </Drawer>

        {/* Inventory Analytics Drawer */}
        <Drawer
          title={
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: "18px" }}>
                {inventoryDrawerType === "shortage"
                  ? "⚠️"
                  : inventoryDrawerType === "overstock"
                  ? "📦"
                  : inventoryDrawerType === "stockAge"
                  ? "📅"
                  : "🔄"}
              </span>
              <span>
                {inventoryDrawerData?.projectName} -{" "}
                {inventoryDrawerType === "shortage"
                  ? "Shortage Materials"
                  : inventoryDrawerType === "overstock"
                  ? "Overstock Materials"
                  : inventoryDrawerType === "stockAge"
                  ? "Stock Aging Analysis"
                  : "Material Turnover"}
              </span>
            </div>
          }
          placement="right"
          width={900}
          onClose={() => setInventoryDrawerVisible(false)}
          open={inventoryDrawerVisible}
        >
          {inventoryDrawerData && (
            <div>
              {inventoryDrawerType === "shortage" && (
                <Table
                  columns={[
                    {
                      title: "Material Name",
                      dataIndex: "materialName",
                      key: "materialName",
                      width: 200,
                      render: (text: string) => (
                        <span style={{ fontWeight: 500 }}>{text}</span>
                      ),
                    },
                    {
                      title: "Planned Qty",
                      dataIndex: "plannedQty",
                      key: "plannedQty",
                      width: 120,
                      render: (qty: number, record: any) => (
                        <span>
                          {qty} {record.unit}
                        </span>
                      ),
                    },
                    {
                      title: "Actual Qty",
                      dataIndex: "actualQty",
                      key: "actualQty",
                      width: 120,
                      render: (qty: number, record: any) => (
                        <span style={{ color: "#faad14", fontWeight: 500 }}>
                          {qty} {record.unit}
                        </span>
                      ),
                    },
                    {
                      title: "Shortage",
                      dataIndex: "shortage",
                      key: "shortage",
                      width: 120,
                      render: (shortage: number, record: any) => (
                        <span style={{ color: "#ff4d4f", fontWeight: 600 }}>
                          {shortage} {record.unit}
                        </span>
                      ),
                    },
                    {
                      title: "Shortage %",
                      dataIndex: "shortagePercent",
                      key: "shortagePercent",
                      width: 100,
                      render: (percent: string) => (
                        <Tag color="red">{percent}</Tag>
                      ),
                    },
                  ]}
                  dataSource={inventoryDrillRows}
                  loading={inventoryDrillLoading}
                  pagination={inventoryDrillPagination}
                  onChange={handleInventoryDrillTableChange}
                  size="small"
                  bordered
                  summary={(pageData) => {
                    return (
                      <Table.Summary fixed>
                        <Table.Summary.Row>
                          <Table.Summary.Cell index={0} colSpan={5}>
                            <div
                              style={{
                                textAlign: "center",
                                fontWeight: 600,
                                color: "#ff4d4f",
                              }}
                            >
                              Total Materials with Shortage: {pageData.length}
                            </div>
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                      </Table.Summary>
                    );
                  }}
                />
              )}

              {inventoryDrawerType === "overstock" && (
                <Table
                  columns={[
                    {
                      title: "Material Name",
                      dataIndex: "materialName",
                      key: "materialName",
                      width: 200,
                      render: (text: string) => (
                        <span style={{ fontWeight: 500 }}>{text}</span>
                      ),
                    },
                    {
                      title: "Planned Qty",
                      dataIndex: "plannedQty",
                      key: "plannedQty",
                      width: 120,
                      render: (qty: number, record: any) => (
                        <span>
                          {qty} {record.unit}
                        </span>
                      ),
                    },
                    {
                      title: "Actual Qty",
                      dataIndex: "actualQty",
                      key: "actualQty",
                      width: 120,
                      render: (qty: number, record: any) => (
                        <span style={{ color: "#faad14", fontWeight: 500 }}>
                          {qty} {record.unit}
                        </span>
                      ),
                    },
                    {
                      title: "Overstock",
                      dataIndex: "overstock",
                      key: "overstock",
                      width: 120,
                      render: (overstock: number, record: any) => (
                        <span style={{ color: "#fa8c16", fontWeight: 600 }}>
                          +{overstock} {record.unit}
                        </span>
                      ),
                    },
                    {
                      title: "Overstock %",
                      dataIndex: "overstockPercent",
                      key: "overstockPercent",
                      width: 100,
                      render: (percent: string) => (
                        <Tag color="orange">{percent}</Tag>
                      ),
                    },
                  ]}
                  dataSource={inventoryDrillRows}
                  loading={inventoryDrillLoading}
                  pagination={inventoryDrillPagination}
                  onChange={handleInventoryDrillTableChange}
                  size="small"
                  bordered
                  summary={(pageData) => {
                    return (
                      <Table.Summary fixed>
                        <Table.Summary.Row>
                          <Table.Summary.Cell index={0} colSpan={5}>
                            <div
                              style={{
                                textAlign: "center",
                                fontWeight: 600,
                                color: "#fa8c16",
                              }}
                            >
                              Total Materials with Overstock: {pageData.length}
                            </div>
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                      </Table.Summary>
                    );
                  }}
                />
              )}

              {inventoryDrawerType === "turnover" && (
                <Table
                  columns={[
                    {
                      title: "Material Name",
                      dataIndex: "materialName",
                      key: "materialName",
                      width: 220,
                      render: (text: string) => (
                        <span style={{ fontWeight: 500 }}>{text}</span>
                      ),
                    },
                    {
                      title: "Delivery Date",
                      dataIndex: "deliveryDate",
                      key: "deliveryDate",
                      width: 130,
                      render: (date: string) => (
                        <span style={{ color: "#52c41a" }}>{date}</span>
                      ),
                    },
                    {
                      title: "Consumption Date",
                      dataIndex: "consumptionDate",
                      key: "consumptionDate",
                      width: 150,
                      render: (date: string) => (
                        <span style={{ color: "#1890ff" }}>{date}</span>
                      ),
                    },
                    {
                      title: "Days in Inventory",
                      dataIndex: "daysInInventory",
                      key: "daysInInventory",
                      width: 140,
                      render: (days: number) => {
                        const color =
                          days > 30
                            ? "#ff4d4f"
                            : days > 20
                            ? "#faad14"
                            : "#52c41a";
                        return (
                          <Tag
                            color={
                              days > 30 ? "red" : days > 20 ? "orange" : "green"
                            }
                          >
                            {days} days
                          </Tag>
                        );
                      },
                    },
                  ]}
                  dataSource={inventoryDrillRows}
                  loading={inventoryDrillLoading}
                  pagination={inventoryDrillPagination}
                  onChange={handleInventoryDrillTableChange}
                  size="small"
                  bordered
                  summary={(pageData: any) => {
                    const totalDays = pageData.reduce(
                      (sum: number, item: any) =>
                        sum + toNumber(item.daysInInventory),
                      0
                    );
                    const avgDays =
                      pageData.length > 0
                        ? (totalDays / pageData.length).toFixed(1)
                        : 0;
                    return (
                      <Table.Summary fixed>
                        <Table.Summary.Row>
                          <Table.Summary.Cell index={0} colSpan={3}>
                            <div
                              style={{ textAlign: "right", fontWeight: 600 }}
                            >
                              Average Days in Inventory:
                            </div>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={3}>
                            <div style={{ fontWeight: 600, color: "#1890ff" }}>
                              {avgDays} days
                            </div>
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                      </Table.Summary>
                    );
                  }}
                />
              )}

              {inventoryDrawerType === "stockAge" && (
                <Table
                  columns={[
                    {
                      title: "Material Name",
                      dataIndex: "materialName",
                      key: "materialName",
                      width: 220,
                      render: (text: string) => (
                        <span style={{ fontWeight: 500 }}>{text}</span>
                      ),
                    },
                    {
                      title: "Delivery Date",
                      dataIndex: "deliveryDate",
                      key: "deliveryDate",
                      width: 130,
                      render: (date: string) => (
                        <span style={{ color: "#52c41a" }}>{date}</span>
                      ),
                    },
                    {
                      title: "Current Stock",
                      dataIndex: "currentStock",
                      key: "currentStock",
                      width: 150,
                      render: (stock: number, record: any) => (
                        <span style={{ color: "#1890ff", fontWeight: 500 }}>
                          {stock} {record.unit}
                        </span>
                      ),
                    },
                    {
                      title: "Aging Days (from Delivery)",
                      dataIndex: "agingDays",
                      key: "agingDays",
                      width: 180,
                      render: (days: number) => {
                        const color =
                          days > 60
                            ? "red"
                            : days > 45
                            ? "orange"
                            : days > 30
                            ? "blue"
                            : "green";
                        return (
                          <Tag color={color} style={{ fontSize: "13px" }}>
                            {days} days
                          </Tag>
                        );
                      },
                    },
                  ]}
                  dataSource={inventoryDrillRows}
                  loading={inventoryDrillLoading}
                  pagination={inventoryDrillPagination}
                  onChange={handleInventoryDrillTableChange}
                  size="small"
                  bordered
                  summary={(pageData: any) => {
                    const totalAgingDays = pageData.reduce(
                      (sum: number, item: any) =>
                        sum + toNumber(item.agingDays),
                      0
                    );
                    const avgAgingDays =
                      pageData.length > 0
                        ? (totalAgingDays / pageData.length).toFixed(1)
                        : 0;
                    return (
                      <Table.Summary fixed>
                        <Table.Summary.Row>
                          <Table.Summary.Cell index={0} colSpan={3}>
                            <div
                              style={{ textAlign: "right", fontWeight: 600 }}
                            >
                              Average Aging Days:
                            </div>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={3}>
                            <div style={{ fontWeight: 600, color: "#1890ff" }}>
                              {avgAgingDays} days
                            </div>
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                      </Table.Summary>
                    );
                  }}
                />
              )}
            </div>
          )}
        </Drawer>

        {/* Budget Analytics Drawer */}
        <Drawer
          title={
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: "18px" }}>💰</span>
              <span>
                {selectedBudgetProject?.project} - Budget Analysis
                (Month-over-Month)
              </span>
            </div>
          }
          placement="right"
          width={1200}
          onClose={() => setBudgetDrawerVisible(false)}
          open={budgetDrawerVisible}
        >
          {selectedBudgetProject && (
            <div>
              <Card size="small" style={{ marginBottom: 16 }}>
                <Row gutter={16}>
                  <Col span={6}>
                    <Statistic
                      title="Total Budget"
                      value={selectedBudgetProject.budget}
                      valueStyle={{ color: "#1890ff", fontSize: "16px" }}
                    />
                  </Col>
                  <Col span={6}>
                    <Statistic
                      title="Committed"
                      value={selectedBudgetProject.committed}
                      valueStyle={{ color: "#722ed1", fontSize: "16px" }}
                    />
                  </Col>
                  <Col span={6}>
                    <Statistic
                      title="Actual"
                      value={selectedBudgetProject.actual}
                      valueStyle={{ color: "#52c41a", fontSize: "16px" }}
                    />
                  </Col>
                  <Col span={6}>
                    <Statistic
                      title="Utilization"
                      value={selectedBudgetProject.utilization}
                      valueStyle={{
                        color:
                          parseInt(selectedBudgetProject.utilization) > 80
                            ? "#ff4d4f"
                            : parseInt(selectedBudgetProject.utilization) > 50
                            ? "#faad14"
                            : "#52c41a",
                        fontSize: "16px",
                      }}
                    />
                  </Col>
                </Row>
              </Card>

              <Table
                dataSource={
                  budgetMomRows.length > 0
                    ? budgetMomRows
                    : selectedBudgetProject.momData
                }
                loading={budgetVarianceLoading}
                pagination={false}
                size="small"
                bordered
                style={{ marginTop: 16 }}
                columns={[
                  {
                    title: "Month",
                    dataIndex: "month",
                    key: "month",
                    width: 120,
                    fixed: "left" as const,
                    render: (text: string) => (
                      <span style={{ fontWeight: 600 }}>{text}</span>
                    ),
                  },
                  {
                    title: "Planned Budget",
                    dataIndex: "plannedBudget",
                    key: "plannedBudget",
                    width: 150,
                    align: "right" as const,
                    render: (value: number) => (
                      <span style={{ color: "#1890ff", fontWeight: 500 }}>
                        {formatCurrency(value)}
                      </span>
                    ),
                  },
                  {
                    title: "Actual Budget",
                    dataIndex: "actualBudget",
                    key: "actualBudget",
                    width: 150,
                    align: "right" as const,
                    render: (value: number) => (
                      <span style={{ color: "#52c41a", fontWeight: 500 }}>
                        {formatCurrency(value)}
                      </span>
                    ),
                  },
                  {
                    title: "Variance",
                    dataIndex: "variance",
                    key: "variance",
                    width: 130,
                    align: "right" as const,
                    render: (value: number) => (
                      <span
                        style={{
                          color: value < 0 ? "#ff4d4f" : "#52c41a",
                          fontWeight: 600,
                        }}
                      >
                        {formatCurrency(value)}
                      </span>
                    ),
                  },
                  {
                    title: "Variance %",
                    dataIndex: "variancePercent",
                    key: "variancePercent",
                    width: 110,
                    align: "center" as const,
                    render: (text: string) => {
                      const isNegative = text.startsWith("-");
                      return (
                        <Tag
                          color={isNegative ? "red" : "green"}
                          style={{ fontWeight: 600 }}
                        >
                          {text}
                        </Tag>
                      );
                    },
                  },
                ]}
                summary={(pageData: any) => {
                  let totalPlanned = 0;
                  let totalActual = 0;
                  let totalVariance = 0;

                  pageData.forEach(({ plannedBudget, actualBudget, variance }: any) => {
                    totalPlanned += plannedBudget;
                    totalActual += actualBudget;
                    totalVariance += variance;
                  });

                  const variancePercent = ((totalVariance / totalPlanned) * 100).toFixed(1);

                  return (
                    <Table.Summary.Row style={{ backgroundColor: "#fafafa" }}>
                      <Table.Summary.Cell index={0}>
                        <strong>Total</strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={1} align="right">
                        <strong style={{ color: "#1890ff" }}>
                          {formatCurrency(totalPlanned)}
                        </strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={2} align="right">
                        <strong style={{ color: "#52c41a" }}>
                          {formatCurrency(totalActual)}
                        </strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={3} align="right">
                        <strong
                          style={{
                            color: totalVariance < 0 ? "#ff4d4f" : "#52c41a",
                          }}
                        >
                          {formatCurrency(totalVariance)}
                        </strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={4} align="center">
                        <Tag
                          color={totalVariance < 0 ? "red" : "green"}
                          style={{ fontWeight: 600, fontSize: "13px" }}
                        >
                          {totalVariance < 0 ? "-" : "+"}{Math.abs(parseFloat(variancePercent))}%
                        </Tag>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  );
                }}
              />

              {(() => {
                const momSource =
                  budgetMomRows.length > 0
                    ? budgetMomRows
                    : selectedBudgetProject.momData || [];
                const totalPlanned = momSource.reduce(
                  (sum: number, item: any) =>
                    sum + Number(item.plannedBudget ?? 0),
                  0
                );
                const totalActual = momSource.reduce(
                  (sum: number, item: any) =>
                    sum + Number(item.actualBudget ?? 0),
                  0
                );
                const overall = totalActual - totalPlanned;
                return (
                  <Card
                    size="small"
                    style={{ marginTop: 16, backgroundColor: "#fafafa" }}
                  >
                    <Row gutter={16}>
                      <Col span={8}>
                        <Statistic
                          title={`Total Planned (${momSource.length} rows)`}
                          value={formatCurrency(totalPlanned)}
                          valueStyle={{ color: "#1890ff", fontSize: "14px" }}
                        />
                      </Col>
                      <Col span={8}>
                        <Statistic
                          title={`Total Actual (${momSource.length} rows)`}
                          value={formatCurrency(totalActual)}
                          valueStyle={{ color: "#52c41a", fontSize: "14px" }}
                        />
                      </Col>
                      <Col span={8}>
                        <Statistic
                          title="Overall Variance"
                          value={formatCurrency(Math.abs(overall))}
                          prefix={overall > 0 ? "+" : overall < 0 ? "-" : ""}
                          valueStyle={{
                            color: overall > 0 ? "#ff4d4f" : "#52c41a",
                            fontSize: "14px",
                          }}
                        />
                      </Col>
                    </Row>
                  </Card>
                );
              })()}
            </div>
          )}
        </Drawer>

        {/* Procurement Overdue Invoices Drawer */}
        <Drawer
          title={
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: "18px" }}>📄</span>
              <span>
                {selectedProcurementProject?.projectName} - Overdue Invoices
              </span>
            </div>
          }
          placement="right"
          width={1000}
          onClose={() => setProcurementDrawerVisible(false)}
          open={procurementDrawerVisible}
        >
          {selectedProcurementProject && (
            <div>
              <Card size="small" style={{ marginBottom: 16 }}>
                <Row gutter={16}>
                  <Col span={8}>
                    <Statistic
                      title="Total Overdue Amount"
                      value={
                        invoiceAgingSummary?.totalOverdueAmount !== undefined
                          ? formatExecCurrency(
                              invoiceAgingSummary.totalOverdueAmount
                            )
                          : selectedProcurementProject.overdueValue
                      }
                      valueStyle={{ color: "#ff4d4f", fontSize: "16px" }}
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="Overdue %"
                      value={
                        invoiceAgingSummary?.overduePct !== undefined
                          ? `${invoiceAgingSummary.overduePct}%`
                          : selectedProcurementProject.overduePercent
                      }
                      valueStyle={{ color: "#ff4d4f", fontSize: "16px" }}
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="Avg Aging Days"
                      value={
                        invoiceAgingSummary?.avgAgingDays ??
                        selectedProcurementProject.avgAgingDays
                      }
                      suffix="days"
                      valueStyle={{
                        color:
                          (invoiceAgingSummary?.avgAgingDays ??
                            selectedProcurementProject.avgAgingDays) > 20
                            ? "#ff4d4f"
                            : (invoiceAgingSummary?.avgAgingDays ??
                                selectedProcurementProject.avgAgingDays) > 15
                            ? "#faad14"
                            : "#1890ff",
                        fontSize: "16px",
                      }}
                    />
                  </Col>
                </Row>
              </Card>

              <Table
                columns={[
                  {
                    title: "Invoice Number",
                    dataIndex: "invoiceNumber",
                    key: "invoiceNumber",
                    width: 150,
                    render: (text: string) => (
                      <span style={{ fontWeight: 600, color: "#1890ff" }}>
                        {text}
                      </span>
                    ),
                  },
                  {
                    title: "Vendor",
                    dataIndex: "vendor",
                    key: "vendor",
                    width: 200,
                    render: (text: string) => (
                      <span style={{ fontWeight: 500 }}>{text}</span>
                    ),
                  },
                  {
                    title: "Invoice Date",
                    dataIndex: "invoiceDate",
                    key: "invoiceDate",
                    width: 120,
                    render: (date: string) => (
                      <span style={{ color: "#52c41a" }}>{date}</span>
                    ),
                  },
                  {
                    title: "Due Date",
                    dataIndex: "dueDate",
                    key: "dueDate",
                    width: 120,
                    render: (date: string) => (
                      <span style={{ color: "#faad14" }}>{date}</span>
                    ),
                  },
                  {
                    title: "Overdue Amount",
                    dataIndex: "overdueAmount",
                    key: "overdueAmount",
                    width: 150,
                    render: (amount: string) => (
                      <span
                        style={{
                          color: "#ff4d4f",
                          fontWeight: 600,
                          fontSize: "14px",
                        }}
                      >
                        {amount}
                      </span>
                    ),
                  },
                  {
                    title: "Aging Days",
                    dataIndex: "agingDays",
                    key: "agingDays",
                    width: 120,
                    render: (days: number) => {
                      let color = "";
                      let text = "";
                      if (days > 30) {
                        color = "red";
                        text = "Critical";
                      } else if (days > 20) {
                        color = "volcano";
                        text = "High";
                      } else if (days > 15) {
                        color = "orange";
                        text = "Medium";
                      } else if (days > 10) {
                        color = "gold";
                        text = "Low";
                      } else {
                        color = "blue";
                        text = "Recent";
                      }
                      return (
                        <div>
                          <Tag
                            color={color}
                            style={{ fontWeight: 600, fontSize: "13px" }}
                          >
                            {days} days
                          </Tag>
                          <div
                            style={{
                              fontSize: "11px",
                              color: "#999",
                              marginTop: "2px",
                            }}
                          >
                            {text}
                          </div>
                        </div>
                      );
                    },
                  },
                ]}
                dataSource={invoiceAgingDetailsList}
                loading={invoiceAgingLoading}
                pagination={invoiceAgingPagination}
                onChange={handleInvoiceAgingTableChange}
                size="small"
                bordered
                scroll={{ y: 400 }}
                summary={(pageData: any) => {
                  const totalOverdue = pageData.reduce(
                    (sum: number, item: any) =>
                      sum + toNumber(item.overdueAmount),
                    0
                  );
                  const totalAgingDays = pageData.reduce(
                    (sum: number, item: any) => sum + toNumber(item.agingDays),
                    0
                  );
                  const avgAgingDays =
                    pageData.length > 0
                      ? (totalAgingDays / pageData.length).toFixed(1)
                      : 0;
                  return (
                    <Table.Summary fixed>
                      <Table.Summary.Row style={{ backgroundColor: "#fafafa" }}>
                        <Table.Summary.Cell index={0} colSpan={4}>
                          <strong style={{ fontSize: "14px" }}>
                            Total ({pageData.length} invoices)
                          </strong>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={4}>
                          <strong
                            style={{ color: "#ff4d4f", fontSize: "14px" }}
                          >
                            ₹{totalOverdue.toLocaleString("en-IN")}
                          </strong>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={5}>
                          <strong style={{ fontSize: "14px" }}>
                            Avg: {avgAgingDays} days
                          </strong>
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    </Table.Summary>
                  );
                }}
              />
            </div>
          )}
        </Drawer>

        {/* RA Bill Aging Analysis Drawer */}
        <Drawer
          title={
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: "18px" }}>📋</span>
              <span>
                {selectedRaBillProject?.projectName} - RA Bill Wise Aging
                Analysis
              </span>
            </div>
          }
          placement="right"
          width={1000}
          onClose={() => setRaBillDrawerVisible(false)}
          open={raBillDrawerVisible}
        >
          {selectedRaBillProject && (
            <div>
              <Card size="small" style={{ marginBottom: 16 }}>
                <Row gutter={16}>
                  <Col span={6}>
                    <Statistic
                      title="Total RA Bills Raised"
                      value={
                        raBillsAgingSummary?.totalPayable !== undefined
                          ? formatExecCurrency(raBillsAgingSummary.totalPayable)
                          : selectedRaBillProject.raBillsRaised
                      }
                      valueStyle={{ color: "#52c41a", fontSize: "16px" }}
                    />
                  </Col>
                  <Col span={6}>
                    <Statistic
                      title="Total Outstanding"
                      value={
                        raBillsAgingSummary?.outstanding !== undefined
                          ? formatExecCurrency(raBillsAgingSummary.outstanding)
                          : selectedRaBillProject.raBillsOutstanding
                      }
                      valueStyle={{ color: "#faad14", fontSize: "16px" }}
                    />
                  </Col>
                  <Col span={6}>
                    <Statistic
                      title="Total Overdue"
                      value={
                        raBillsAgingSummary?.overdue !== undefined
                          ? formatExecCurrency(raBillsAgingSummary.overdue)
                          : selectedRaBillProject.raBillsOverdue
                      }
                      valueStyle={{ color: "#ff4d4f", fontSize: "16px" }}
                    />
                  </Col>
                  <Col span={6}>
                    <Statistic
                      title="Avg Aging Days"
                      value={
                        raBillsAgingSummary?.avgAgingDays ??
                        selectedRaBillProject.avgAgingDays
                      }
                      suffix="days"
                      valueStyle={{
                        color:
                          (raBillsAgingSummary?.avgAgingDays ??
                            selectedRaBillProject.avgAgingDays) > 20
                            ? "#ff4d4f"
                            : (raBillsAgingSummary?.avgAgingDays ??
                                selectedRaBillProject.avgAgingDays) > 15
                            ? "#faad14"
                            : "#52c41a",
                        fontSize: "16px",
                      }}
                    />
                  </Col>
                </Row>
              </Card>

              <Table
                columns={[
                  {
                    title: "RA Bill Number",
                    dataIndex: "raBillNumber",
                    key: "raBillNumber",
                    width: 150,
                    render: (text: string) => (
                      <span style={{ fontWeight: 600, color: "#1890ff" }}>
                        {text}
                      </span>
                    ),
                  },
                  {
                    title: "Contractor",
                    dataIndex: "contractor",
                    key: "contractor",
                    width: 200,
                    render: (text: string) => (
                      <span style={{ fontWeight: 500 }}>🏢 {text}</span>
                    ),
                  },
                  {
                    title: "Raised Date",
                    dataIndex: "raisedDate",
                    key: "raisedDate",
                    width: 120,
                    render: (date: string) => (
                      <span style={{ color: "#52c41a" }}>{date}</span>
                    ),
                  },
                  {
                    title: "Due Date",
                    dataIndex: "dueDate",
                    key: "dueDate",
                    width: 120,
                    render: (date: string) => (
                      <span style={{ color: "#faad14" }}>{date}</span>
                    ),
                  },
                  {
                    title: "Overdue Amount",
                    dataIndex: "overdueAmount",
                    key: "overdueAmount",
                    width: 150,
                    render: (amount: string) => (
                      <span
                        style={{
                          color: "#ff4d4f",
                          fontWeight: 600,
                          fontSize: "14px",
                        }}
                      >
                        {amount}
                      </span>
                    ),
                  },
                  {
                    title: "Aging Days",
                    dataIndex: "agingDays",
                    key: "agingDays",
                    width: 120,
                    render: (days: number) => {
                      let color = "";
                      let text = "";
                      if (days > 30) {
                        color = "red";
                        text = "Critical";
                      } else if (days > 20) {
                        color = "volcano";
                        text = "High";
                      } else if (days > 15) {
                        color = "orange";
                        text = "Medium";
                      } else if (days > 10) {
                        color = "gold";
                        text = "Low";
                      } else {
                        color = "blue";
                        text = "Recent";
                      }
                      return (
                        <div>
                          <Tag
                            color={color}
                            style={{ fontWeight: 600, fontSize: "13px" }}
                          >
                            {days} days
                          </Tag>
                          <div
                            style={{
                              fontSize: "11px",
                              color: "#999",
                              marginTop: "2px",
                            }}
                          >
                            {text}
                          </div>
                        </div>
                      );
                    },
                  },
                ]}
                dataSource={raBillsAgingList}
                loading={raBillsAgingLoading}
                pagination={raBillsAgingPagination}
                onChange={handleRaBillsAgingTableChange}
                size="small"
                bordered
                scroll={{ y: 400 }}
                summary={(pageData: any) => {
                  if (pageData.length === 0) return null;
                  const totalOverdue = pageData.reduce(
                    (sum: number, item: any) =>
                      sum + toNumber(item.overdueAmount),
                    0
                  );
                  const totalAgingDays = pageData.reduce(
                    (sum: number, item: any) => sum + toNumber(item.agingDays),
                    0
                  );
                  const avgAgingDays =
                    pageData.length > 0
                      ? (totalAgingDays / pageData.length).toFixed(1)
                      : 0;
                  return (
                    <Table.Summary fixed>
                      <Table.Summary.Row style={{ backgroundColor: "#fafafa" }}>
                        <Table.Summary.Cell index={0} colSpan={4}>
                          <strong style={{ fontSize: "14px" }}>
                            Total ({pageData.length} RA Bills)
                          </strong>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={4}>
                          <strong
                            style={{ color: "#ff4d4f", fontSize: "14px" }}
                          >
                            ₹{totalOverdue.toLocaleString("en-IN")}
                          </strong>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={5}>
                          <strong style={{ fontSize: "14px" }}>
                            Avg: {avgAgingDays} days
                          </strong>
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    </Table.Summary>
                  );
                }}
              />
            </div>
          )}
        </Drawer>

        {/* PO Details Drawer */}
        <Drawer
          title={
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: "18px" }}>📦</span>
              <span>
                {selectedPoProject?.projectName} - Purchase Orders Breakup
              </span>
            </div>
          }
          placement="right"
          width={1000}
          onClose={() => setPoDrawerVisible(false)}
          open={poDrawerVisible}
        >
          {selectedPoProject && (
            <div>
              <Card size="small" style={{ marginBottom: 16 }}>
                <Row gutter={16}>
                  <Col span={8}>
                    <Statistic
                      title="Total PO Count"
                      value={
                        poSummary?.totalPoCount ?? selectedPoProject.poCount
                      }
                      suffix="POs"
                      valueStyle={{ color: "#1890ff", fontSize: "16px" }}
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="Total PO Value"
                      value={
                        poSummary?.totalPoValue !== undefined
                          ? formatExecCurrency(poSummary.totalPoValue)
                          : selectedPoProject.poValue
                      }
                      valueStyle={{ color: "#1890ff", fontSize: "16px" }}
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="Received Value"
                      value={
                        poSummary?.receivedValue !== undefined
                          ? formatExecCurrency(poSummary.receivedValue)
                          : "-"
                      }
                      valueStyle={{ color: "#52c41a", fontSize: "16px" }}
                    />
                  </Col>
                </Row>
              </Card>

              <Table
                columns={[
                  {
                    title: "PO Number",
                    dataIndex: "poNumber",
                    key: "poNumber",
                    width: 150,
                    render: (text: string) => (
                      <span style={{ fontWeight: 600, color: "#1890ff" }}>
                        {text}
                      </span>
                    ),
                  },
                  {
                    title: "Vendor",
                    dataIndex: "vendor",
                    key: "vendor",
                    width: 200,
                    render: (text: string) => (
                      <span style={{ fontWeight: 500 }}>🏢 {text}</span>
                    ),
                  },
                  {
                    title: "PO Date",
                    dataIndex: "poDate",
                    key: "poDate",
                    width: 120,
                    render: (date: string) => (
                      <span style={{ color: "#1890ff" }}>{date}</span>
                    ),
                  },
                  {
                    title: "PO Value",
                    dataIndex: "poValue",
                    key: "poValue",
                    width: 130,
                    render: (value: string) => (
                      <span style={{ fontWeight: 600, fontSize: "14px" }}>
                        {value}
                      </span>
                    ),
                  },
                  {
                    title: "Received Value",
                    dataIndex: "receivedValue",
                    key: "receivedValue",
                    width: 140,
                    render: (value: string) => (
                      <span
                        style={{
                          color: "#52c41a",
                          fontWeight: 600,
                          fontSize: "14px",
                        }}
                      >
                        {value}
                      </span>
                    ),
                  },
                  {
                    title: "Status",
                    dataIndex: "status",
                    key: "status",
                    width: 140,
                    render: (status: string) => {
                      let color = "";
                      if (status === "Received") {
                        color = "green";
                      } else if (status === "Partially Received") {
                        color = "orange";
                      } else if (status === "Pending") {
                        color = "red";
                      }
                      return (
                        <Tag
                          color={color}
                          style={{ fontWeight: 600, fontSize: "13px" }}
                        >
                          {status}
                        </Tag>
                      );
                    },
                  },
                ]}
                dataSource={poDetails}
                loading={poDrillLoading}
                pagination={poPagination}
                onChange={handlePoTableChange}
                size="small"
                bordered
                scroll={{ y: 400 }}
                summary={(pageData: any) => {
                  if (pageData.length === 0) return null;
                  const totalPoValue = pageData.reduce(
                    (sum: number, item: any) => sum + toNumber(item.poValue),
                    0
                  );
                  const totalReceivedValue = pageData.reduce(
                    (sum: number, item: any) =>
                      sum + toNumber(item.receivedValue),
                    0
                  );
                  return (
                    <Table.Summary fixed>
                      <Table.Summary.Row style={{ backgroundColor: "#fafafa" }}>
                        <Table.Summary.Cell index={0} colSpan={3}>
                          <strong style={{ fontSize: "14px" }}>
                            Total ({pageData.length} POs)
                          </strong>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={3}>
                          <strong style={{ fontSize: "14px" }}>
                            ₹{totalPoValue.toLocaleString("en-IN")}
                          </strong>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={4}>
                          <strong
                            style={{ color: "#52c41a", fontSize: "14px" }}
                          >
                            ₹{totalReceivedValue.toLocaleString("en-IN")}
                          </strong>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={5}>
                          <strong style={{ fontSize: "14px" }}>
                            {(
                              (totalReceivedValue / totalPoValue) *
                              100
                            ).toFixed(1)}
                            % Received
                          </strong>
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    </Table.Summary>
                  );
                }}
              />
            </div>
          )}
        </Drawer>

        {/* Invoice Payables Drawer */}
        <Drawer
          title={
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: "18px" }}>💰</span>
              <span>
                {selectedInvoicePayablesProject?.projectName} - Invoice Payables
                with Due Days
              </span>
            </div>
          }
          placement="right"
          width={1000}
          onClose={() => setInvoicePayablesDrawerVisible(false)}
          open={invoicePayablesDrawerVisible}
        >
          {selectedInvoicePayablesProject && (
            <div>
              <Card size="small" style={{ marginBottom: 16 }}>
                <Row gutter={16}>
                  <Col span={8}>
                    <Statistic
                      title="Total Invoices"
                      value={
                        invoiceSummary?.totalInvoices ??
                        selectedInvoicePayablesProject.invoicePayablesCount
                      }
                      suffix="Invoices"
                      valueStyle={{ color: "#722ed1", fontSize: "16px" }}
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="Total Payables"
                      value={
                        invoiceSummary?.totalPayables !== undefined
                          ? formatExecCurrency(invoiceSummary.totalPayables)
                          : selectedInvoicePayablesProject.invoicePayablesValue
                      }
                      valueStyle={{ color: "#722ed1", fontSize: "16px" }}
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="Overdue Invoices"
                      value={
                        invoiceSummary?.overdueInvoices ??
                        invoiceDetailsList.filter(
                          (inv: any) => inv.status === "Overdue"
                        ).length
                      }
                      valueStyle={{ color: "#ff4d4f", fontSize: "16px" }}
                    />
                  </Col>
                </Row>
              </Card>

              <Table
                columns={[
                  {
                    title: "Invoice Number",
                    dataIndex: "invoiceNumber",
                    key: "invoiceNumber",
                    width: 150,
                    render: (text: string) => (
                      <span style={{ fontWeight: 600, color: "#722ed1" }}>
                        {text}
                      </span>
                    ),
                  },
                  {
                    title: "Vendor",
                    dataIndex: "vendor",
                    key: "vendor",
                    width: 200,
                    render: (text: string) => (
                      <span style={{ fontWeight: 500 }}>🏢 {text}</span>
                    ),
                  },
                  {
                    title: "Invoice Date",
                    dataIndex: "invoiceDate",
                    key: "invoiceDate",
                    width: 120,
                    render: (date: string) => (
                      <span style={{ color: "#52c41a" }}>{date}</span>
                    ),
                  },
                  {
                    title: "Due Date",
                    dataIndex: "dueDate",
                    key: "dueDate",
                    width: 120,
                    render: (date: string) => (
                      <span style={{ color: "#faad14" }}>{date}</span>
                    ),
                  },
                  {
                    title: "Amount",
                    dataIndex: "amount",
                    key: "amount",
                    width: 130,
                    render: (amount: string) => (
                      <span style={{ fontWeight: 600, fontSize: "14px" }}>
                        {amount}
                      </span>
                    ),
                  },
                  {
                    title: "Due Days",
                    dataIndex: "dueDays",
                    key: "dueDays",
                    width: 100,
                    render: (days: number) => {
                      let color = "";
                      if (days > 30) {
                        color = "red";
                      } else if (days > 15) {
                        color = "volcano";
                      } else if (days > 7) {
                        color = "orange";
                      } else {
                        color = "blue";
                      }
                      return (
                        <Tag
                          color={color}
                          style={{ fontWeight: 600, fontSize: "13px" }}
                        >
                          {days} days
                        </Tag>
                      );
                    },
                  },
                  {
                    title: "Status",
                    dataIndex: "status",
                    key: "status",
                    width: 120,
                    render: (status: string) => {
                      let color = "";
                      if (status === "Overdue") {
                        color = "red";
                      } else if (status === "Due Soon") {
                        color = "orange";
                      } else {
                        color = "blue";
                      }
                      return (
                        <Tag
                          color={color}
                          style={{ fontWeight: 600, fontSize: "13px" }}
                        >
                          {status}
                        </Tag>
                      );
                    },
                  },
                ]}
                dataSource={invoiceDetailsList}
                loading={invoiceDrillLoading}
                pagination={invoicePagination}
                onChange={handleInvoiceTableChange}
                size="small"
                bordered
                scroll={{ y: 400 }}
                summary={(pageData: any) => {
                  if (pageData.length === 0) return null;
                  const totalAmount = pageData.reduce(
                    (sum: number, item: any) => sum + toNumber(item.amount),
                    0
                  );
                  const avgDueDays =
                    pageData.length > 0
                      ? (
                          pageData.reduce(
                            (sum: number, item: any) =>
                              sum + toNumber(item.dueDays),
                            0
                          ) / pageData.length
                        ).toFixed(1)
                      : 0;
                  return (
                    <Table.Summary fixed>
                      <Table.Summary.Row style={{ backgroundColor: "#fafafa" }}>
                        <Table.Summary.Cell index={0} colSpan={4}>
                          <strong style={{ fontSize: "14px" }}>
                            Total ({pageData.length} invoices)
                          </strong>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={4}>
                          <strong style={{ fontSize: "14px" }}>
                            ₹{totalAmount.toLocaleString("en-IN")}
                          </strong>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={5}>
                          <strong style={{ fontSize: "14px" }}>
                            Avg: {avgDueDays} days
                          </strong>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={6}>
                          <strong
                            style={{ color: "#ff4d4f", fontSize: "14px" }}
                          >
                            {
                              pageData.filter(
                                (item: any) => item.status === "Overdue"
                              ).length
                            }{" "}
                            Overdue
                          </strong>
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    </Table.Summary>
                  );
                }}
              />
            </div>
          )}
        </Drawer>

        {/* RA Bill Payables Drawer with Due Days */}
        <Drawer
          title={
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: "18px" }}>💵</span>
              <span>
                {selectedRaBillPayablesProject?.projectName} - RA Bills Payable
                with Due Days
              </span>
            </div>
          }
          placement="right"
          width={1000}
          onClose={() => setRaBillPayablesDrawerVisible(false)}
          open={raBillPayablesDrawerVisible}
        >
          {selectedRaBillPayablesProject && (
            <div>
              <Card size="small" style={{ marginBottom: 16 }}>
                <Row gutter={16}>
                  <Col span={6}>
                    <Statistic
                      title="Total RA Bills"
                      value={
                        raBillsPayableSummary?.totalRaBills ??
                        raBillsPayableList.length
                      }
                      suffix="Bills"
                      valueStyle={{ color: "#722ed1", fontSize: "16px" }}
                    />
                  </Col>
                  <Col span={6}>
                    <Statistic
                      title="Total Payable"
                      value={
                        raBillsPayableSummary?.totalPayable !== undefined
                          ? formatExecCurrency(
                              raBillsPayableSummary.totalPayable
                            )
                          : selectedRaBillPayablesProject.raBillsPayable
                      }
                      valueStyle={{ color: "#722ed1", fontSize: "16px" }}
                    />
                  </Col>
                  <Col span={6}>
                    <Statistic
                      title="Outstanding"
                      value={
                        raBillsPayableSummary?.outstanding !== undefined
                          ? formatExecCurrency(
                              raBillsPayableSummary.outstanding
                            )
                          : selectedRaBillPayablesProject.raBillsOutstanding
                      }
                      valueStyle={{ color: "#faad14", fontSize: "16px" }}
                    />
                  </Col>
                  <Col span={6}>
                    <Statistic
                      title="Overdue"
                      value={
                        raBillsPayableSummary?.overdue !== undefined
                          ? formatExecCurrency(raBillsPayableSummary.overdue)
                          : selectedRaBillPayablesProject.raBillsOverdue
                      }
                      valueStyle={{ color: "#ff4d4f", fontSize: "16px" }}
                    />
                  </Col>
                </Row>
              </Card>

              <Table
                columns={[
                  {
                    title: "RA Bill Number",
                    dataIndex: "raBillNumber",
                    key: "raBillNumber",
                    width: 150,
                    render: (text: string) => (
                      <span style={{ fontWeight: 600, color: "#722ed1" }}>
                        {text}
                      </span>
                    ),
                  },
                  {
                    title: "Contractor",
                    dataIndex: "contractor",
                    key: "contractor",
                    width: 200,
                    render: (text: string) => (
                      <span style={{ fontWeight: 500 }}>🏢 {text}</span>
                    ),
                  },
                  {
                    title: "Raised Date",
                    dataIndex: "raisedDate",
                    key: "raisedDate",
                    width: 120,
                    render: (date: string) => (
                      <span style={{ color: "#52c41a" }}>{date}</span>
                    ),
                  },
                  {
                    title: "Due Date",
                    dataIndex: "dueDate",
                    key: "dueDate",
                    width: 120,
                    render: (date: string) => (
                      <span style={{ color: "#faad14" }}>{date}</span>
                    ),
                  },
                  {
                    title: "Amount",
                    dataIndex: "amount",
                    key: "amount",
                    width: 120,
                    render: (amount: string) => (
                      <span style={{ fontWeight: 600, fontSize: "14px" }}>
                        {amount}
                      </span>
                    ),
                  },
                  {
                    title: "Due Days",
                    dataIndex: "dueDays",
                    key: "dueDays",
                    width: 100,
                    render: (days: number) => {
                      let color = "";
                      if (days > 30) {
                        color = "red";
                      } else if (days > 20) {
                        color = "volcano";
                      } else if (days > 10) {
                        color = "orange";
                      } else {
                        color = "blue";
                      }
                      return (
                        <Tag
                          color={color}
                          style={{ fontWeight: 600, fontSize: "13px" }}
                        >
                          {days} days
                        </Tag>
                      );
                    },
                  },
                  {
                    title: "Status",
                    dataIndex: "status",
                    key: "status",
                    width: 120,
                    render: (status: string) => {
                      let color = "";
                      if (status === "Overdue") {
                        color = "red";
                      } else if (status === "Due Soon") {
                        color = "orange";
                      } else {
                        color = "blue";
                      }
                      return (
                        <Tag
                          color={color}
                          style={{ fontWeight: 600, fontSize: "13px" }}
                        >
                          {status}
                        </Tag>
                      );
                    },
                  },
                ]}
                dataSource={raBillsPayableList}
                loading={raBillsPayableLoading}
                pagination={raBillsPayablePagination}
                onChange={handleRaBillsPayableTableChange}
                size="small"
                bordered
                scroll={{ y: 400 }}
                summary={(pageData: any) => {
                  if (pageData.length === 0) return null;
                  const totalAmount = pageData.reduce(
                    (sum: number, item: any) => sum + toNumber(item.amount),
                    0
                  );
                  const avgDueDays =
                    pageData.length > 0
                      ? (
                          pageData.reduce(
                            (sum: number, item: any) =>
                              sum + toNumber(item.dueDays),
                            0
                          ) / pageData.length
                        ).toFixed(1)
                      : 0;
                  const overdueCount = pageData.filter(
                    (item: any) => item.status === "Overdue"
                  ).length;
                  return (
                    <Table.Summary fixed>
                      <Table.Summary.Row style={{ backgroundColor: "#fafafa" }}>
                        <Table.Summary.Cell index={0} colSpan={4}>
                          <strong style={{ fontSize: "14px" }}>
                            Total ({pageData.length} RA Bills)
                          </strong>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={4}>
                          <strong style={{ fontSize: "14px" }}>
                            ₹{totalAmount.toLocaleString("en-IN")}
                          </strong>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={5}>
                          <strong style={{ fontSize: "14px" }}>
                            Avg: {avgDueDays} days
                          </strong>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={6}>
                          <strong
                            style={{ color: "#ff4d4f", fontSize: "14px" }}
                          >
                            {overdueCount} Overdue
                          </strong>
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    </Table.Summary>
                  );
                }}
              />
            </div>
          )}
        </Drawer>

        {/* Budget Variance Drivers Drawer */}
        <Drawer
          title={
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: "18px" }}>📊</span>
              <span>
                {selectedVarianceProject?.project} - Budget Variance Drivers
              </span>
            </div>
          }
          placement="right"
          width={950}
          onClose={() => setVarianceDrawerVisible(false)}
          open={varianceDrawerVisible}
        >
          {selectedVarianceProject && (
            <div>
              <Card size="small" style={{ marginBottom: 16 }}>
                <Row gutter={16}>
                  <Col span={8}>
                    <Statistic
                      title="Total Budget"
                      value={selectedVarianceProject.budget}
                      valueStyle={{ color: "#1890ff", fontSize: "16px" }}
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="Actual Spend"
                      value={selectedVarianceProject.actual}
                      valueStyle={{ color: "#52c41a", fontSize: "16px" }}
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="Total Variance"
                      value={selectedVarianceProject.variance}
                      valueStyle={{
                        // Use the raw number — the formatted string starts with
                        // "₹", not "-", so a string check is unreliable.
                        color:
                          toNumber(
                            selectedVarianceProject.varianceRaw ??
                              selectedVarianceProject.variance
                          ) < 0
                            ? "#52c41a"
                            : "#ff4d4f",
                        fontSize: "16px",
                      }}
                    />
                  </Col>
                </Row>
              </Card>

              <Table
                columns={[
                  {
                    title: "Budget Category",
                    dataIndex: "category",
                    key: "category",
                    width: 200,
                    render: (text: string) => (
                      <span style={{ fontWeight: 600 }}>{text}</span>
                    ),
                  },
                  {
                    title: "Planned",
                    dataIndex: "planned",
                    key: "planned",
                    width: 150,
                    render: (amount: number) => (
                      <span style={{ color: "#1890ff", fontWeight: 500 }}>
                        {formatCurrency(amount)}
                      </span>
                    ),
                  },
                  {
                    title: "Actual",
                    dataIndex: "actual",
                    key: "actual",
                    width: 150,
                    render: (amount: number) => (
                      <span style={{ color: "#52c41a", fontWeight: 500 }}>
                        {formatCurrency(amount)}
                      </span>
                    ),
                  },
                  {
                    title: "Variance",
                    dataIndex: "variance",
                    key: "variance",
                    width: 150,
                    render: (variance: number) => {
                      const isPositive = variance > 0;
                      return (
                        <span
                          style={{
                            color: isPositive ? "#ff4d4f" : "#52c41a",
                            fontWeight: 600,
                          }}
                        >
                          {isPositive ? "+" : ""}
                          {formatCurrency(Math.abs(variance))}
                        </span>
                      );
                    },
                  },
                  {
                    title: "Variance %",
                    dataIndex: "variancePercent",
                    key: "variancePercent",
                    width: 120,
                    render: (percent: string) => {
                      const isPositive = percent.startsWith("+");
                      return (
                        <Tag
                          color={isPositive ? "red" : "green"}
                          style={{ fontSize: "13px", fontWeight: 500 }}
                        >
                          {percent}
                        </Tag>
                      );
                    },
                  },
                  {
                    title: "Impact",
                    key: "impact",
                    width: 130,
                    render: (record: any) => {
                      const variance = Math.abs(record.variance);
                      let impact = "";
                      let color = "";
                      if (variance > 1000000) {
                        impact = "High Impact";
                        color = "red";
                      } else if (variance > 500000) {
                        impact = "Medium Impact";
                        color = "orange";
                      } else if (variance > 100000) {
                        impact = "Low Impact";
                        color = "blue";
                      } else {
                        impact = "Minimal";
                        color = "green";
                      }
                      return <Tag color={color}>{impact}</Tag>;
                    },
                  },
                ]}
                dataSource={
                  budgetVarianceList.length > 0
                    ? budgetVarianceList
                    : selectedVarianceProject.varianceData
                }
                loading={budgetVarianceLoading}
                pagination={budgetVariancePagination}
                onChange={handleBudgetVarianceTableChange}
                size="small"
                bordered
                scroll={{ y: 400 }}
                summary={(pageData: any) => {
                  const totalPlanned = pageData.reduce(
                    (sum: number, item: any) => sum + item.planned,
                    0
                  );
                  const totalActual = pageData.reduce(
                    (sum: number, item: any) => sum + item.actual,
                    0
                  );
                  const totalVariance = totalActual - totalPlanned;
                  const variancePercent =
                    totalPlanned > 0
                      ? ((totalVariance / totalPlanned) * 100).toFixed(2)
                      : "0";
                  return (
                    <Table.Summary fixed>
                      <Table.Summary.Row style={{ backgroundColor: "#fafafa" }}>
                        <Table.Summary.Cell index={0}>
                          <strong style={{ fontSize: "14px" }}>Total</strong>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={1}>
                          <strong
                            style={{ color: "#1890ff", fontSize: "14px" }}
                          >
                            {formatCurrency(totalPlanned)}
                          </strong>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={2}>
                          <strong
                            style={{ color: "#52c41a", fontSize: "14px" }}
                          >
                            {formatCurrency(totalActual)}
                          </strong>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={3}>
                          <strong
                            style={{
                              color: totalVariance > 0 ? "#ff4d4f" : "#52c41a",
                              fontSize: "14px",
                            }}
                          >
                            {totalVariance > 0 ? "+" : ""}
                            {formatCurrency(Math.abs(totalVariance))}
                          </strong>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={4}>
                          <strong style={{ fontSize: "14px" }}>
                            {totalVariance > 0 ? "+" : ""}
                            {variancePercent}%
                          </strong>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={5}>
                          <Tag
                            color={
                              Math.abs(totalVariance) > 2000000
                                ? "red"
                                : Math.abs(totalVariance) > 1000000
                                ? "orange"
                                : "green"
                            }
                          >
                            <strong>
                              {Math.abs(totalVariance) > 2000000
                                ? "High Impact"
                                : Math.abs(totalVariance) > 1000000
                                ? "Medium Impact"
                                : "Low Impact"}
                            </strong>
                          </Tag>
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    </Table.Summary>
                  );
                }}
              />
            </div>
          )}
        </Drawer>
      </Content>
    </React.Fragment>
  );
};

export default ConsolidatedDashboard;
